/**
 * Meta Flow 工具函數
 * 用於處理 Meta WhatsApp Flows JSON 的生成、解析和驗證
 * 
 * 重要：所有組件格式必須嚴格按照 Meta 官方文檔：
 * https://developers.facebook.com/docs/whatsapp/flows/reference/flowjson
 * https://developers.facebook.com/docs/whatsapp/flows/reference/components
 */

import { 
  COMPONENT_SPECS,
  getComponentSpec, 
  validateComponent, 
  getComponentsRequiringTerminal,
  getComponentsRequiringDataModel,
  extractDataSourceName,
  generateDataModel
} from './metaFlowComponentSpecs';

/**
 * 判斷編輯器中兩個 action 是否為同一組件（用於更新 / 刪除時對齊）。
 * 優先比對 id；若僅一方有 id（例如從 Flow JSON 匯入時沒有內部 id），則以 name（Meta 表單欄位名）對齊。
 */
export const isSameFlowAction = (comp, sel) => {
  if (!comp || !sel) return false;
  if (sel.id && comp.id) return comp.id === sel.id;
  if (sel.id && !comp.id) return sel.name != null && comp.name === sel.name;
  if (!sel.id && comp.id) return comp.name != null && comp.name === sel.name;
  return (comp.id || comp.name) === (sel.id || sel.name);
};

/**
 * 編碼 Dataset 配置信息
 * 用於將 Dataset 配置嵌入到變量名中
 * 由於 Meta 變量名只允許字母和下劃線，我們使用自定義的 Hex 編碼：
 * 1. 轉為 Hex
 * 2. 將數字 0-9 替換為字母 g-p
 */
const encodeDatasetConfig = (config) => {
  try {
    const minConfig = {
      d: config.datasetId,
      k: config.idField,
      v: config.titleField
    };
    const jsonStr = JSON.stringify(minConfig);
    
    // 轉為 Hex
    let hex = '';
    for (let i = 0; i < jsonStr.length; i++) {
      hex += jsonStr.charCodeAt(i).toString(16).padStart(2, '0');
    }
    
    // 將數字 0-9 映射為 g-p
    // 0->g, 1->h, 2->i, 3->j, 4->k, 5->l, 6->m, 7->n, 8->o, 9->p
    const encoded = hex.split('').map(char => {
      if (char >= '0' && char <= '9') {
        return String.fromCharCode(char.charCodeAt(0) - '0'.charCodeAt(0) + 'g'.charCodeAt(0));
      }
      return char;
    }).join('');

    return encoded;
  } catch (e) {
    console.error('編碼 Dataset 配置失敗:', e);
    return '';
  }
};

/**
 * 解碼 Dataset 配置信息
 */
const decodeDatasetConfig = (str) => {
  try {
    // 還原數字：g-p -> 0-9
    const hex = str.split('').map(char => {
      if (char >= 'g' && char <= 'p') {
        return String.fromCharCode(char.charCodeAt(0) - 'g'.charCodeAt(0) + '0'.charCodeAt(0));
      }
      return char;
    }).join('');

    // Hex 轉 String
    let jsonStr = '';
    for (let i = 0; i < hex.length; i += 2) {
      jsonStr += String.fromCharCode(parseInt(hex.substr(i, 2), 16));
    }

    const minConfig = JSON.parse(jsonStr);
    return {
      datasetId: minConfig.d,
      idField: minConfig.k,
      titleField: minConfig.v
    };
  } catch (e) {
    // console.error('解碼 Dataset 配置失敗:', e); 
    return null;
  }
};

/**
 * meta_collection 配置編碼：對應 CommerceCollectionsPage（後台 commerce_collections）
 * 編碼結構：{ c: collectionId, l: limit }
 * 後端會根據 collectionId 從 commerce_collection_products 取得關聯商品，並以
 * id = product id, title = `${product name} - ${unit}` 形式回傳。
 */
const encodeMetaCollectionConfig = (config) => {
  try {
    const minConfig = {
      c: config.collectionId || config.catalogId, // 兼容：舊資料可能傳入 catalogId
      l: Number(config.limit || 30)
    };
    const jsonStr = JSON.stringify(minConfig);
    let hex = '';
    for (let i = 0; i < jsonStr.length; i++) {
      hex += jsonStr.charCodeAt(i).toString(16).padStart(2, '0');
    }
    return hex.split('').map(char => {
      if (char >= '0' && char <= '9') {
        return String.fromCharCode(char.charCodeAt(0) - '0'.charCodeAt(0) + 'g'.charCodeAt(0));
      }
      return char;
    }).join('');
  } catch (e) {
    console.error('編碼 Meta Collection 配置失敗:', e);
    return '';
  }
};

const decodeMetaCollectionConfig = (str) => {
  try {
    const hex = str.split('').map(char => {
      if (char >= 'g' && char <= 'p') {
        return String.fromCharCode(char.charCodeAt(0) - 'g'.charCodeAt(0) + '0'.charCodeAt(0));
      }
      return char;
    }).join('');
    let jsonStr = '';
    for (let i = 0; i < hex.length; i += 2) {
      jsonStr += String.fromCharCode(parseInt(hex.substr(i, 2), 16));
    }
    const minConfig = JSON.parse(jsonStr);
    return {
      collectionId: minConfig.c,
      limit: minConfig.l
    };
  } catch {
    return null;
  }
};

/**
 * 生成默認 Screen
 */
export const getDefaultScreen = (id = null) => {
  const screenId = id || createDefaultScreenId();
  return {
    id: screenId,
    title: '新 Screen',
    data: {
      body: {
        type: 'body',
        text: '請輸入內容'
      },
      footer: {
        type: 'footer',
        text: '提交' // Footer 是必填項，設置默認值
      },
      header: {
        type: 'header',
        format: 'TEXT',
        text: '' // header 不能為 null，必須是對象
      },
      actions: []
    }
  };
};

/**
 * 生成唯一的組件 name
 * 例如：Dropdown, Dropdown1, Dropdown2, ...
 */
const generateUniqueComponentName = (type, existingComponents = []) => {
  // 組件類型到基礎名稱的映射
  const typeToBaseName = {
    'select': 'Dropdown',
    'checkbox': 'CheckboxGroup',
    'radio': 'RadioButtonsGroup',
    'chips_selector': 'ChipsSelector',
    'text_input': 'TextInput',
    'text_area': 'text_area',  // 使用小寫避免與元件類型 "TextArea" 混淆，與 text_input 命名風格一致
    'date_picker': 'DatePicker',
    'calendar_picker': 'CalendarPicker',
    'time_picker': 'TimePicker',
    'photo_picker': 'PhotoPicker',
    'document_picker': 'DocumentPicker'
  };
  
  const baseName = typeToBaseName[type] || type;
  
  // 收集現有組件的所有 name
  const existingNames = new Set();
  existingComponents.forEach(comp => {
    if (comp.name) {
      existingNames.add(comp.name);
    }
    // 也檢查 data.name（某些組件可能將 name 存在 data 中）
    if (comp.data?.name) {
      existingNames.add(comp.data.name);
    }
  });
  
  // 如果基礎名稱可用，直接使用
  if (!existingNames.has(baseName)) {
    return baseName;
  }
  
  // 否則嘗試 baseName1, baseName2, ...
  let counter = 1;
  let uniqueName = `${baseName}${counter}`;
  while (existingNames.has(uniqueName)) {
    counter++;
    uniqueName = `${baseName}${counter}`;
  }
  
  return uniqueName;
};

/**
 * 生成默認組件
 * @param {string} type - 組件類型
 * @param {string|null} id - 可選的組件 ID
 * @param {Array} existingComponents - 現有組件列表（用於生成唯一的 name）
 */
export const getDefaultComponent = (type, id = null, existingComponents = []) => {
  const componentId = id || `${type}_${Date.now()}`;
  
  // 生成唯一的 name（對於需要 name 的組件）
  const uniqueName = generateUniqueComponentName(type, existingComponents);
  
  switch (type) {
    // 文本組件
    case 'text_heading':
      return {
        type: 'text_heading',
        text: '標題文本'
      };
    
    case 'text_body':
      return {
        type: 'text_body',
        text: '正文內容'
      };
    
    case 'footer':
      return {
        type: 'footer',
        text: '頁腳文本',
        action: {
          type: 'submit',
          payload: {}
        }
      };
    
    // 輸入組件
    case 'text_input':
      return {
        type: 'text_input',
        id: componentId,
        name: componentId,
        title: '文字輸入',
        data: {
          input_type: 'text',
          required: false,
          default_value: ''
        },
        action: {
          type: 'navigate',
          next: ''
        }
      };
    
    case 'text_area':
      return {
        type: 'text_area',
        id: componentId,
        name: uniqueName,
        title: '多行文字',
        data: {
          required: false,
          max_length: '600',
          helper_text: '',
          default_value: ''
        },
        action: {
          type: 'navigate',
          next: ''
        }
      };
    
    case 'rich_text':
      return {
        type: 'rich_text',
        id: componentId,
        title: '富文本顯示',
        data: {
          // RichText 使用 text 數組，支持 Markdown 語法
          text: ['請輸入富文本內容', '支持 *粗體*、_斜體_、~刪除線~ 等 Markdown 語法']
        }
      };
    
    case 'date_picker':
      return {
        type: 'date_picker',
        id: componentId, // 添加 id 以便在 UI 中識別和操作
        name: uniqueName, // name 用於 Meta JSON，使用唯一名稱
        title: '日期選擇',
        data: {
          required: false
        },
        action: {
          type: 'submit',
          payload: {}
        }
      };
    
    case 'calendar_picker':
      return {
        type: 'calendar_picker',
        id: componentId, // 添加 id 以便在 UI 中識別和操作
        name: uniqueName, // name 用於 Meta JSON，使用唯一名稱
        title: '日曆選擇',
        data: {
          required: false
        },
        action: {
          type: 'submit',
          payload: {}
        }
      };
    
    case 'time_picker':
      return {
        type: 'time_picker',
        name: uniqueName, // 使用唯一名稱
        title: '時間選擇',
        data: {
          required: false
        },
        action: {
          type: 'submit',
          payload: {}
        }
      };
    
    // 選擇組件
    case 'select':
      // 為每個 Dropdown 組件生成唯一的 data-source 名稱
      const dropdownDataSourceName = `dropdown_${uniqueName.replace(/[^a-zA-Z0-9_]/g, '_')}`;
      return {
        type: 'select',
        id: componentId, // 添加 id 以便在 UI 中識別和操作
        name: uniqueName, // Dropdown 使用 name 而不是 id（用於 Meta JSON），使用唯一名稱
        title: '下拉選擇',
        data: {
          data_source: `\${data.${dropdownDataSourceName}}`, // 使用唯一的 data-source 名稱
          required: false
        },
        action: {
          type: 'submit',
          payload: {}
        }
      };
    
    case 'checkbox':
      // 為每個 CheckboxGroup 組件生成唯一的 data-source 名稱
      const checkboxDataSourceName = `checkbox_${uniqueName.replace(/[^a-zA-Z0-9_]/g, '_')}`;
      return {
        type: 'checkbox',
        id: componentId, // 添加 id 以便在 UI 中識別和操作
        name: uniqueName, // CheckboxGroup 使用 name 而不是 id（用於 Meta JSON），使用唯一名稱
        title: '複選框組',
        data: {
          data_source: `\${data.${checkboxDataSourceName}}`, // 使用唯一的 data-source 名稱
          required: false
        },
        action: {
          type: 'submit',
          payload: {}
        }
      };
    
    case 'radio':
      // 為每個 RadioButtonsGroup 組件生成唯一的 data-source 名稱
      const radioDataSourceName = `radio_${uniqueName.replace(/[^a-zA-Z0-9_]/g, '_')}`;
      return {
        type: 'radio',
        id: componentId, // 添加 id 以便在 UI 中識別和操作
        name: uniqueName, // RadioButtonsGroup 使用 name 而不是 id（用於 Meta JSON），使用唯一名稱
        title: '單選框組',
        data: {
          data_source: `\${data.${radioDataSourceName}}`, // 使用唯一的 data-source 名稱
          required: false
        },
        action: {
          type: 'submit',
          payload: {}
        }
      };
    
    case 'chips_selector':
      return {
        type: 'chips_selector',
        id: componentId,
        name: uniqueName, // ChipsSelector 使用 name，使用唯一名稱
        title: '小標籤選擇器',
        data: {
          options: [], // 不提供默認值，讓用戶自己添加
          required: false,
          max_selected_items: 2,
          description: ''
        },
        action: {
          type: 'submit',
          payload: {}
        }
      };
    
    // 按鈕組件
    case 'button':
      return {
        type: 'button',
        id: componentId,
        title: '按鈕',
        action: {
          type: 'navigate',
          next: ''
        }
      };
    
    // 媒體組件
    case 'image':
      return {
        type: 'image',
        id: componentId,
        title: '圖片',
        data: {
          url: '', // 編輯器中使用 url，生成 JSON 時轉換為 src
          src: '', // 同時保存 src
          width: 200, // 默認寬度
          height: 200 // 默認高度
        },
        action: {
          type: 'navigate',
          next: ''
        }
      };
    
    case 'video':
      return {
        type: 'video',
        id: componentId,
        title: '視頻',
        data: {
          url: '',
          thumbnail_url: ''
        },
        action: {
          type: 'navigate',
          next: ''
        }
      };
    
    case 'document':
      return {
        type: 'document',
        id: componentId,
        title: '文檔',
        data: {
          url: '',
          filename: 'document.pdf'
        },
        action: {
          type: 'navigate',
          next: ''
        }
      };
    
    // 媒體上傳組件（從 Flow JSON version 4.0 開始支持）
    case 'photo_picker':
      return {
        type: 'photo_picker',
        id: componentId, // 添加 id 以便在 UI 中識別和操作
        name: uniqueName, // name 用於 Meta JSON，使用唯一名稱
        title: '照片選擇器',
        data: {
          label: '請選擇照片',
          // description 不包含空字符串，只在有值時才添加
          photo_source: 'camera_gallery',
          max_file_size_kb: 25600, // Meta API 限制：單個組件的 max-file-size-kb 最大值為 25600 KB，所有組件的總和不能超過 102400 KB
          min_uploaded_photos: 0,
          max_uploaded_photos: 3, // 默認值：3 張照片
          enabled: true,
          visible: true
          // error-message 不包含空字符串，只在有值時才添加
        },
        action: {
          type: 'submit',
          payload: {}
        }
      };
    
    case 'document_picker':
      return {
        type: 'document_picker',
        id: componentId, // 添加 id 以便在 UI 中識別和操作
        name: uniqueName, // name 用於 Meta JSON，使用唯一名稱
        title: '文檔選擇器',
        data: {
          label: '請選擇文檔',
          // description 不包含空字符串，只在有值時才添加
          max_file_size_kb: 25600, // Meta API 限制：單個組件的 max-file-size-kb 最大值為 25600 KB，所有組件的總和不能超過 102400 KB
          min_uploaded_documents: 0,
          max_uploaded_documents: 10, // Meta API 限制：所有組件的總文件數量不能超過 10
          allowed_mime_types: ['application/pdf', 'image/jpeg', 'image/png'],
          enabled: true,
          visible: true
          // error-message 不包含空字符串，只在有值時才添加
        },
        action: {
          type: 'submit',
          payload: {}
        }
      };
    
    // 鏈接組件
    case 'embedded_link':
      return {
        type: 'embedded_link',
        text: '點擊這裡',
        action: {
          type: 'url',
          endpoint: 'https://example.com'
        }
      };
    
    case 'opt_in':
      return {
        type: 'opt_in',
        label: '我同意條款',
        name: 'terms_agreement',
        action: {
          type: 'url',
          endpoint: 'https://example.com/terms'
        }
      };
    
    // 邏輯組件
    case 'if':
      return {
        type: 'if',
        key: '${form.field_name}',
        components: []
      };
    
    case 'switch':
      return {
        type: 'switch',
        key: '${form.field_name}',
        cases: [
          {
            key: 'value1',
            components: []
          }
        ]
      };
    
    // 容器組件
    case 'navigation_list':
      return {
        type: 'navigation_list',
        id: componentId,
        title: '導航列表',
        data: {
          items: [
            { id: 'item_1', title: '項目 1', description: '描述 1' },
            { id: 'item_2', title: '項目 2', description: '描述 2' }
          ]
        },
        action: {
          type: 'navigate',
          next: ''
        }
      };
    
    case 'image_carousel':
      // 根據官方文檔：最少 1 張圖片，最多 3 張圖片
      // 參考：https://developers.facebook.com/docs/whatsapp/flows/reference/components#image_carousel
      return {
        type: 'image_carousel',
        id: componentId,
        title: '圖片輪播',
        data: {
          images: [
            { src: '', 'alt-text': '圖片 1' }
          ],
          'aspect-ratio': '4:3',
          'scale-type': 'contain'
        }
      };
    
    default:
      return {
        type: type,
        id: componentId,
        title: '組件'
      };
  }
};

/**
 * 清理 ID/name，保留字母、數字與底線（表單欄位 name 如 text_input_1770443624451 須保留數字）
 */
const cleanId = (id) => {
  if (!id) return '';
  return String(id).replace(/[^a-zA-Z0-9_]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '') || 'component';
};

/**
 * 生成只包含字母與底線的默認 screen id（Meta 編輯器要求）
 */
const createDefaultScreenId = () => {
  const letters = String(Date.now()).split('').map(d => String.fromCharCode(97 + Number(d))).join('');
  const random = (Math.random().toString(36).replace(/[^a-z]/g, '').slice(0, 4) || 'flow');
  return `screen_${letters}_${random}`;
};

/**
 * 清理 screen id，只保留字母與底線（符合 Meta 編輯器限制）
 */
const cleanScreenId = (id) => {
  if (!id) return '';
  return String(id).replace(/[^a-zA-Z_]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '') || 'screen';
};

const getAlphabeticSuffix = (index) => {
  let n = index;
  let result = '';
  do {
    result = String.fromCharCode(97 + (n % 26)) + result;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return result;
};

const createScreenIdMap = (screens = []) => {
  const idMap = new Map();
  const used = new Set();

  screens.forEach((screen, index) => {
    const originalId = screen?.id || `screen_${index}`;
    const baseId = cleanScreenId(originalId) || 'screen';
    let candidate = baseId;
    let duplicateIndex = 0;

    while (used.has(candidate)) {
      candidate = `${baseId}_${getAlphabeticSuffix(duplicateIndex)}`;
      duplicateIndex += 1;
    }

    used.add(candidate);
    idMap.set(originalId, candidate);
  });

  return idMap;
};

/**
 * 處理 next 值，轉換為官方格式 { name: string, type: "screen" }
 */
const processNextValue = (next) => {
  if (!next) return null;
  
  if (typeof next === 'string') {
    const cleanedName = cleanId(next) || 'screen';
    return { name: cleanedName, type: 'screen' };
  }
  
  if (typeof next === 'object' && next !== null) {
    const nextName = next.name || '';
    const cleanedName = cleanId(nextName) || 'screen';
    return { 
      name: cleanedName, 
      type: next.type || 'screen' 
    };
  }
  
  return null;
};

/**
 * 處理 on-click-action，根據 action 類型生成正確的格式
 */
const processOnClickAction = (action) => {
  if (!action || !action.action) {
    return { name: 'data_exchange', payload: {} };
  }
  
  const actionType = action.action.type;
  const payload = action.action.payload || {};
  
  switch (actionType) {
    case 'submit':
      return { name: 'data_exchange', payload };
    
    case 'navigate':
      const nextValue = processNextValue(action.action.next);
      if (nextValue && nextValue.name) {
        return {
          name: 'navigate',
          next: nextValue,
          payload
        };
      }
      return { name: 'data_exchange', payload };
    
    case 'url':
      return {
        name: 'open_url',
        url: action.action.endpoint || '',
        payload
      };
    
    default:
      return { name: 'data_exchange', payload };
  }
};

/**
 * 官方組件映射表
 * 根據 Meta 官方文檔定義的組件格式
 * 參考：https://developers.facebook.com/docs/whatsapp/flows/reference/components
 */
const OFFICIAL_COMPONENT_MAP = {
  // 文本組件
  'text_heading': {
    type: 'TextHeading',
    requiredFields: ['text'],
    optionalFields: []
  },
  'text_body': {
    type: 'TextBody',
    requiredFields: ['text'],
    optionalFields: []
  },
  'footer': {
    type: 'Footer',
    requiredFields: ['label', 'on-click-action'],
    optionalFields: []
  },
  
  // 輸入組件
  'text_input': {
    type: 'TextInput',
    requiredFields: ['name', 'label'],
    optionalFields: ['input-type', 'required', 'helper-text', 'pattern']
  },
  'text_area': {
    type: 'TextArea',
    requiredFields: ['name', 'label'],
    optionalFields: ['required', 'max-length', 'helper-text', 'enabled', 'visible']
  },
  'rich_text': {
    type: 'RichText',
    requiredFields: ['text'],
    optionalFields: ['visible']
  },
  'date_picker': {
    type: 'DatePicker',
    requiredFields: ['name', 'label'],
    optionalFields: ['required', 'on-select-action', 'enabled', 'visible', 'description', 'error-message']
  },
  'calendar_picker': {
    type: 'CalendarPicker',
    requiredFields: ['name', 'label'],
    optionalFields: ['required', 'on-select-action', 'enabled', 'visible', 'description', 'error-message']
  },
  
  // 選擇組件
  'select': {
    type: 'Select',
    requiredFields: ['id', 'label', 'options'],
    optionalFields: ['on-click-action']
  },
  'checkbox': {
    type: 'CheckboxGroup',
    requiredFields: ['name', 'label', 'data-source'], // 使用 name 和 data-source 而不是 id 和 options
    optionalFields: ['required', 'on-select-action'] // 使用 on-select-action 而不是 on-click-action
  },
  'radio': {
    type: 'RadioButtonsGroup',
    requiredFields: ['name', 'label', 'data-source'], // 使用 name 和 data-source 而不是 id 和 options
    optionalFields: ['required', 'on-select-action'] // 使用 on-select-action 而不是 on-click-action
  },
  'chips_selector': {
    type: 'ChipsSelector',
    requiredFields: ['name', 'label', 'data-source'], // 使用 name 和 data-source（內聯數組）
    optionalFields: ['required', 'max-selected-items', 'description', 'on-select-action']
  },
  
  // 按鈕組件
  'button': {
    type: 'Button',
    requiredFields: ['id', 'label'],
    optionalFields: ['on-click-action']
  },
  
  // 媒體組件
  'image': {
    type: 'Image',
    requiredFields: ['src'], // 根據官方文檔，Image 使用 src 而不是 url，不支持 id、alt、on-click-action
    optionalFields: ['width', 'height']
  },
  'image_carousel': {
    type: 'ImageCarousel',
    requiredFields: ['images'], // 根據官方文檔，ImageCarousel 不需要 id
    optionalFields: ['aspect-ratio', 'scale-type']
  },
  'video': {
    type: 'Video',
    requiredFields: ['id', 'url'],
    optionalFields: ['thumbnail_url', 'on-click-action']
  },
  'document': {
    type: 'Document',
    requiredFields: ['id', 'url'],
    optionalFields: ['filename', 'on-click-action']
  },
  
  // 媒體上傳組件（從 Flow JSON version 4.0 開始支持）
  'photo_picker': {
    type: 'PhotoPicker',
    requiredFields: ['name', 'label'],
    optionalFields: ['description', 'photo-source', 'max-file-size-kb', 'min-uploaded-photos', 'max-uploaded-photos', 'enabled', 'visible', 'error-message']
  },
  'document_picker': {
    type: 'DocumentPicker',
    requiredFields: ['name', 'label'],
    optionalFields: ['description', 'max-file-size-kb', 'min-uploaded-documents', 'max-uploaded-documents', 'allowed-mime-types', 'enabled', 'visible', 'error-message']
  },
  
  // 鏈接組件
  'embedded_link': {
    type: 'EmbeddedLink',
    requiredFields: ['text', 'on-click-action'],
    optionalFields: []
  },
  'opt_in': {
    type: 'OptIn',
    requiredFields: ['label', 'name', 'on-click-action'],
    optionalFields: []
  },
  
  // 邏輯組件
  'if': {
    type: 'If',
    requiredFields: ['key', 'components'],
    optionalFields: []
  },
  'switch': {
    type: 'Switch',
    requiredFields: ['key', 'cases'],
    optionalFields: []
  },
  
  // 容器組件
  'navigation_list': {
    type: 'NavigationList',
    requiredFields: ['id', 'items'],
    optionalFields: []
  }
};

/**
 * 將編輯器中的 action 轉換為官方組件格式
 * 嚴格按照 Meta 官方文檔格式
 */
const convertActionToComponent = (action) => {
  if (!action || !action.type) return null;
  
  const componentDef = OFFICIAL_COMPONENT_MAP[action.type];
  if (!componentDef) {
    console.warn(`未知的組件類型: ${action.type}`);
    return null;
  }
  
  const cleanedId = cleanId(action.id);
  const cleanedName = cleanId(action.name || action.id);
  
  // 根據組件類型生成對應的 JSON
  switch (action.type) {
    // 文本組件
    case 'text_heading':
      return {
        type: 'TextHeading',
        text: action.text || ''
      };
    
    case 'text_body':
      return {
        type: 'TextBody',
        text: action.text || ''
      };
    
    // 輸入組件（在 Form 內部，使用 name 而不是 id，不使用 on-click-action）
    case 'text_input':
      const textInputName = cleanedName || cleanedId || `text_input_${Date.now()}`;
      const textInputComponent = {
        type: 'TextInput',
        name: textInputName,
        label: action.title || '文字輸入',
        required: action.data?.required || false
      };
      
      // input-type 屬性（注意是連字符，不是下劃線）
      if (action.data?.input_type) {
        textInputComponent['input-type'] = action.data.input_type;
      }
      
      // 可選屬性（注意：TextInput 不支持 placeholder）
      if (action.data?.pattern) {
        textInputComponent.pattern = action.data.pattern;
      }
      if (action.data?.helper_text) {
        textInputComponent['helper-text'] = action.data.helper_text;
      }
      
      return textInputComponent;
    
    case 'text_area':
      const textAreaName = cleanedName || cleanedId || `text_area_${Date.now()}`;
      const textAreaComponent = {
        type: 'TextArea',
        name: textAreaName,
        label: action.title || '多行文字',
        required: action.data?.required || false
      };
      // Meta API 要求 max-length 為 integer，不可為 string
      const maxLengthRaw = action.data?.max_length;
      if (typeof maxLengthRaw === 'string' && (maxLengthRaw.startsWith('${') || maxLengthRaw.startsWith('{{'))) {
        textAreaComponent['max-length'] = maxLengthRaw;
      } else {
        const parsed = parseInt(maxLengthRaw, 10);
        textAreaComponent['max-length'] = Number.isNaN(parsed) ? 600 : Math.max(1, Math.min(600, parsed));
      }
      if (action.data?.helper_text) {
        textAreaComponent['helper-text'] = action.data.helper_text;
      }
      return textAreaComponent;
    
    case 'rich_text':
      // RichText 使用 text 數組，支持 Markdown 語法
      // 如果 action.data.text 是數組，直接使用；如果是字符串，轉換為數組
      let richTextArray = [];
      if (action.data?.text) {
        if (Array.isArray(action.data.text)) {
          richTextArray = action.data.text;
        } else {
          // 將字符串按換行符分割為數組
          richTextArray = action.data.text.split('\n').filter(line => line.trim() !== '');
        }
      } else {
        // 默認內容
        richTextArray = ['請輸入富文本內容'];
      }
      
      return {
        type: 'RichText',
        text: richTextArray
      };
    
    // 日期選擇組件
    case 'date_picker':
      // 根據官方文檔，DatePicker 使用 name 而不是 id，使用 on-select-action
      const datePickerName = cleanId(action.name || action.id || `date_picker_${Date.now()}`);
      
      // DatePicker 使用 on-select-action，需要處理 action.action
      let datePickerSelectAction = null;
      if (action.action) {
        if (action.action.type === 'submit' || action.action.type === 'navigate') {
          datePickerSelectAction = {
            name: action.action.type === 'submit' ? 'data_exchange' : 'navigate',
            payload: action.action.payload || {}
          };
          if (action.action.type === 'navigate' && action.action.next) {
            const nextValue = processNextValue(action.action.next);
            if (nextValue) {
              datePickerSelectAction.next = {
                name: nextValue,
                type: 'screen'
              };
            }
          }
        } else {
          // 如果 action.action.type 不是 submit 或 navigate，也使用 data_exchange
          // 因為 DatePicker 通常需要 data_exchange 來觸發服務器請求
          datePickerSelectAction = {
            name: 'data_exchange',
            payload: action.action.payload || {}
          };
        }
      } else {
        // 默認使用 data_exchange（根據官方文檔，DatePicker 使用 data_exchange 時需要 data_api_version）
        datePickerSelectAction = {
          name: 'data_exchange',
          payload: {}
        };
      }
      
      const datePickerComponent = {
        type: 'DatePicker',
        name: datePickerName,
        label: action.title || '日期選擇',
        required: action.data?.required || false
      };
      
      // 添加可選屬性
      if (action.data?.enabled !== undefined) {
        datePickerComponent.enabled = action.data.enabled;
      }
      if (action.data?.visible !== undefined) {
        datePickerComponent.visible = action.data.visible;
      }
      if (action.data?.description && action.data.description.trim() !== '') {
        datePickerComponent.description = action.data.description;
      }
      if (action.data?.error_message) {
        if (typeof action.data.error_message === 'object') {
          datePickerComponent['error-message'] = action.data.error_message;
        } else if (typeof action.data.error_message === 'string' && action.data.error_message.trim() !== '') {
          datePickerComponent['error-message'] = { text: action.data.error_message };
        }
      }
      
      datePickerComponent['on-select-action'] = datePickerSelectAction;
      
      return datePickerComponent;
    
    // 選擇組件
    case 'select':
      // 根據官方文檔，Dropdown 使用 data-source 而不是 options
      // Dropdown 使用 name 而不是 id，使用 on-select-action 而不是 on-click-action
      const dropdownName = cleanedName || cleanedId || `dropdown_${Date.now()}`;
      if (!dropdownName || dropdownName.trim() === '') {
        console.warn('Dropdown 組件 name 不能為空，已跳過');
        return null;
      }
      
      // 確保 name 不為空
      const finalDropdownName = dropdownName && dropdownName.trim() !== '' 
        ? dropdownName 
        : `dropdown_${Date.now()}`;
      
      // 檢查是否使用 Dataset
      const useDataset = action.data?.optionsSource === 'dataset';
      const useMetaCollection = action.data?.optionsSource === 'meta_collection';
      
      let dropdownDataSource;
      let dropdownAction;

      if (useDataset && action.data?.datasetId) {
        // 使用 Dataset
        const config = {
          datasetId: action.data.datasetId,
          idField: action.data.idField,
          titleField: action.data.titleField
        };
        const encodedConfig = encodeDatasetConfig(config);
        // 使用動態變量作為 data-source
        dropdownDataSource = `\${data.DS_${encodedConfig}}`;
        
        // 強制使用 data_exchange，並在 payload 中攜帶 dataset 信息
        // 注意：Action payload 主要是為了觸發後端處理
        dropdownAction = {
          name: 'data_exchange',
          payload: {
            ...(action.action?.payload || {}),
            _dataset: config // 後端可讀取此信息
          }
        };
      } else if (useMetaCollection && (action.data?.metaCollectionId || action.data?.metaCatalogId)) {
        const config = {
          collectionId: action.data.metaCollectionId || action.data.metaCatalogId,
          limit: action.data.metaCollectionLimit || 30
        };
        const encodedConfig = encodeMetaCollectionConfig(config);
        dropdownDataSource = `\${data.MC_${encodedConfig}}`;
        dropdownAction = {
          name: 'data_exchange',
          payload: {
            ...(action.action?.payload || {}),
            _meta_collection: config
          }
        };
      } else {
        // 使用靜態選項 (Manual)
        // Dropdown 使用靜態 data-source 數組（與 example 一樣）
        // 將 options 轉換為 data-source 格式：[{ "id": "...", "title": "..." }]
        dropdownDataSource = (action.data?.options || []).map(opt => ({
          id: opt.id || opt.value || '',
          title: opt.title || opt.text || ''
        }));
        
        // Dropdown 的 on-select-action 應該使用 update_data 或 data_exchange
        dropdownAction = action.action?.type === 'submit' || action.action?.type === 'navigate'
          ? {
              name: 'update_data', // Dropdown 使用 update_data 而不是 data_exchange
              payload: action.action?.payload || {}
            }
          : {
              name: 'update_data',
              payload: {}
            };
      }
      
      return {
        type: 'Dropdown',
        name: finalDropdownName,
        label: action.title || '下拉選擇',
        'data-source': dropdownDataSource, // 可能為靜態數組或變量引用
        required: action.data?.required || false,
        'on-select-action': dropdownAction
      };
    
    case 'checkbox':
      // 根據官方文檔，CheckboxGroup 使用 name 和 data-source 而不是 id 和 options
      // CheckboxGroup 使用 on-select-action 而不是 on-click-action
      const checkboxName = cleanedName || cleanedId || `checkbox_${Date.now()}`;
      if (!checkboxName || checkboxName.trim() === '') {
        console.warn('CheckboxGroup 組件 name 不能為空，已跳過');
        return null;
      }
      
      // 確保 name 不為空
      const finalCheckboxName = checkboxName && checkboxName.trim() !== '' 
        ? checkboxName 
        : `checkbox_${Date.now()}`;
      
      // 檢查是否使用 Dataset
      const useDatasetCheckbox = action.data?.optionsSource === 'dataset';
      const useMetaCollectionCheckbox = action.data?.optionsSource === 'meta_collection';
      
      let checkboxDataSource;
      let checkboxAction;

      if (useDatasetCheckbox && action.data?.datasetId) {
        // 使用 Dataset
        const config = {
          datasetId: action.data.datasetId,
          idField: action.data.idField,
          titleField: action.data.titleField
        };
        const encodedConfig = encodeDatasetConfig(config);
        checkboxDataSource = `\${data.DS_${encodedConfig}}`;
        
        checkboxAction = {
          name: 'data_exchange',
          payload: {
            ...(action.action?.payload || {}),
            _dataset: config
          }
        };
      } else if (useMetaCollectionCheckbox && (action.data?.metaCollectionId || action.data?.metaCatalogId)) {
        const config = {
          collectionId: action.data.metaCollectionId || action.data.metaCatalogId,
          limit: action.data.metaCollectionLimit || 30
        };
        const encodedConfig = encodeMetaCollectionConfig(config);
        checkboxDataSource = `\${data.MC_${encodedConfig}}`;
        checkboxAction = {
          name: 'data_exchange',
          payload: {
            ...(action.action?.payload || {}),
            _meta_collection: config
          }
        };
      } else {
        // CheckboxGroup 的 on-select-action 應該使用 update_data 或 data_exchange
        checkboxAction = action.action?.type === 'submit' || action.action?.type === 'navigate'
          ? {
              name: 'update_data', // CheckboxGroup 使用 update_data
              payload: action.action?.payload || {}
            }
          : {
              name: 'update_data',
              payload: {}
            };
            
        // CheckboxGroup 使用靜態 data-source 數組
        checkboxDataSource = (action.data?.options || []).map(opt => ({
          id: opt.id || opt.value || '',
          title: opt.title || opt.text || ''
        }));
      }
      
      return {
        type: 'CheckboxGroup',
        name: finalCheckboxName,
        label: action.title || '複選框組',
        'data-source': checkboxDataSource, 
        required: action.data?.required || false,
        'on-select-action': checkboxAction
      };
    
    case 'radio':
      // 根據官方文檔，RadioButtonsGroup 使用 name 和 data-source 而不是 id 和 options
      // RadioButtonsGroup 使用 on-select-action 而不是 on-click-action
      const radioName = cleanedName || cleanedId || `radio_${Date.now()}`;
      if (!radioName || radioName.trim() === '') {
        console.warn('RadioButtonsGroup 組件 name 不能為空，已跳過');
        return null;
      }
      
      // 確保 name 不為空
      const finalRadioName = radioName && radioName.trim() !== '' 
        ? radioName 
        : `radio_${Date.now()}`;
      
      // 檢查是否使用 Dataset
      const useDatasetRadio = action.data?.optionsSource === 'dataset';
      const useMetaCollectionRadio = action.data?.optionsSource === 'meta_collection';
      
      let radioDataSource;
      let radioAction;

      if (useDatasetRadio && action.data?.datasetId) {
        // 使用 Dataset
        const config = {
          datasetId: action.data.datasetId,
          idField: action.data.idField,
          titleField: action.data.titleField
        };
        const encodedConfig = encodeDatasetConfig(config);
        radioDataSource = `\${data.DS_${encodedConfig}}`;
        
        radioAction = {
          name: 'data_exchange',
          payload: {
            ...(action.action?.payload || {}),
            _dataset: config
          }
        };
      } else if (useMetaCollectionRadio && (action.data?.metaCollectionId || action.data?.metaCatalogId)) {
        const config = {
          collectionId: action.data.metaCollectionId || action.data.metaCatalogId,
          limit: action.data.metaCollectionLimit || 30
        };
        const encodedConfig = encodeMetaCollectionConfig(config);
        radioDataSource = `\${data.MC_${encodedConfig}}`;
        radioAction = {
          name: 'data_exchange',
          payload: {
            ...(action.action?.payload || {}),
            _meta_collection: config
          }
        };
      } else {
        // RadioButtonsGroup 的 on-select-action 應該使用 update_data 或 data_exchange
        radioAction = action.action?.type === 'submit' || action.action?.type === 'navigate'
          ? {
              name: 'update_data', // RadioButtonsGroup 使用 update_data
              payload: action.action?.payload || {}
            }
          : {
              name: 'update_data',
              payload: {}
            };
            
        // RadioButtonsGroup 使用靜態 data-source 數組
        radioDataSource = (action.data?.options || []).map(opt => ({
          id: opt.id || opt.value || '',
          title: opt.title || opt.text || ''
        }));
      }
      
      return {
        type: 'RadioButtonsGroup',
        name: finalRadioName,
        label: action.title || '單選框組',
        'data-source': radioDataSource, 
        required: action.data?.required || false,
        'on-select-action': radioAction
      };
    
    // 按鈕組件
    // 注意：根據之前的 API 錯誤，Button 類型可能不支持，暫時移除
    case 'button':
      console.warn('Button 組件在當前 Meta API 版本中可能不支持，已跳過');
      return null;
    
    // 媒體組件
    case 'image':
      // 根據官方文檔，Image 組件使用 src 而不是 url，不支持 id、alt、on-click-action
      // src 應該是純 base64 字符串，不包含 data:image/xxx;base64, 前綴
      let imageSrc = action.data?.src || '';
      
      // 如果 src 是完整 data URL，提取純 base64 字符串
      if (imageSrc.startsWith('data:image/')) {
        imageSrc = imageSrc.includes(',') ? imageSrc.split(',')[1] : '';
      } else if (!imageSrc && action.data?.url) {
        // 如果 src 為空但 url 存在，嘗試從 url 提取
        const url = action.data.url;
        if (url.startsWith('data:image/')) {
          imageSrc = url.includes(',') ? url.split(',')[1] : '';
        } else {
          imageSrc = url; // 如果 url 不是 data URL，直接使用
        }
      }
      
      const imageComponent = {
        type: 'Image',
        src: imageSrc // 純 base64 字符串，符合官方文檔
      };
      
      // 添加可選的 width 和 height
      if (action.data?.width !== undefined && action.data?.width !== null) {
        imageComponent.width = action.data.width;
      }
      if (action.data?.height !== undefined && action.data?.height !== null) {
        imageComponent.height = action.data.height;
      }
      
      return imageComponent;
    
    // 注意：Video 和 Document 不在官方支持的組件列表中，暫時移除
    case 'video':
      console.warn('Video 組件不在官方支持的組件列表中，已跳過');
      return null;
    
    case 'document':
      console.warn('Document 組件不在官方支持的組件列表中，已跳過');
      return null;
    
    // 媒體上傳組件
    case 'photo_picker':
      const photoPickerComponent = {
        type: 'PhotoPicker',
        name: cleanedName,
        label: action.data?.label || action.title || '請選擇照片',
        'photo-source': action.data?.photo_source || 'camera_gallery',
        'max-file-size-kb': action.data?.max_file_size_kb ?? 25600, // Meta API 限制：單個組件的 max-file-size-kb 最大值為 25600 KB，所有組件的總和不能超過 102400 KB
        'min-uploaded-photos': action.data?.min_uploaded_photos ?? 0,
        'max-uploaded-photos': action.data?.max_uploaded_photos ?? 3, // 默認值：3 張照片
        enabled: action.data?.enabled !== undefined ? action.data.enabled : true,
        visible: action.data?.visible !== undefined ? action.data.visible : true
      };
      
      // description: 如果提供且非空，才包含該屬性
      if (action.data?.description && action.data.description.trim() !== '') {
        photoPickerComponent.description = action.data.description;
      }
      
      // error-message: 如果提供且是對象格式，才包含該屬性
      if (action.data?.error_message) {
        if (typeof action.data.error_message === 'object') {
          photoPickerComponent['error-message'] = action.data.error_message;
        } else if (typeof action.data.error_message === 'string' && action.data.error_message.trim() !== '') {
          // 如果是字符串，轉換為對象格式
          photoPickerComponent['error-message'] = { text: action.data.error_message };
        }
      }
      
      return photoPickerComponent;
    
    case 'document_picker':
      // 根據 Meta API 限制，需要檢查總文件數量和總文件大小
      // 這裡先使用用戶設置的值，在 generateMetaFlowJson 中會進行總體驗證
      const documentPickerMaxUploaded = action.data?.max_uploaded_documents ?? 10; // Meta API 限制：所有組件的總文件數量不能超過 10
      const documentPickerMaxFileSize = action.data?.max_file_size_kb ?? 25600; // Meta API 限制：單個組件的 max-file-size-kb 最大值為 25600 KB，所有組件的總和不能超過 102400 KB
      
      const documentPickerComponent = {
        type: 'DocumentPicker',
        name: cleanedName,
        label: action.data?.label || action.title || '請選擇文檔',
        'max-file-size-kb': documentPickerMaxFileSize,
        'min-uploaded-documents': action.data?.min_uploaded_documents ?? 0,
        'max-uploaded-documents': documentPickerMaxUploaded,
        'allowed-mime-types': action.data?.allowed_mime_types || [
          'application/pdf',
          'image/jpeg',
          'image/png'
        ],
        enabled: action.data?.enabled !== undefined ? action.data.enabled : true,
        visible: action.data?.visible !== undefined ? action.data.visible : true
      };
      
      // description: 如果提供且非空，才包含該屬性
      if (action.data?.description && action.data.description.trim() !== '') {
        documentPickerComponent.description = action.data.description;
      }
      
      // error-message: 如果提供且是對象格式，才包含該屬性
      if (action.data?.error_message) {
        if (typeof action.data.error_message === 'object') {
          documentPickerComponent['error-message'] = action.data.error_message;
        } else if (typeof action.data.error_message === 'string' && action.data.error_message.trim() !== '') {
          // 如果是字符串，轉換為對象格式
          documentPickerComponent['error-message'] = { text: action.data.error_message };
        }
      }
      
      return documentPickerComponent;
    
    // 鏈接組件
    case 'embedded_link':
      const embeddedLinkAction = action.action?.type === 'url' 
        ? {
            name: 'open_url',
            url: action.action.endpoint || ''
          }
        : { name: 'open_url', url: '' };
      
      return {
        type: 'EmbeddedLink',
        text: action.text || '',
        'on-click-action': embeddedLinkAction
      };
    
    case 'opt_in':
      const optInAction = action.action?.type === 'url'
        ? {
            name: 'open_url',
            url: action.action.endpoint || ''
          }
        : { name: 'open_url', url: '' };
      
      return {
        type: 'OptIn',
        label: action.label || '',
        name: cleanedName || 'opt_in',
        'on-click-action': optInAction
      };
    
    // 邏輯組件
    case 'if':
      return {
        type: 'If',
        key: action.key || '${form.field_name}',
        components: (action.components || []).map(comp => convertActionToComponent(comp)).filter(Boolean)
      };
    
    case 'switch':
      return {
        type: 'Switch',
        key: action.key || '${form.field_name}',
        cases: (action.cases || []).map(caseItem => ({
          key: caseItem.key || '',
          components: (caseItem.components || []).map(comp => convertActionToComponent(comp)).filter(Boolean)
        }))
      };
    
    // 容器組件
    case 'navigation_list':
      const navListItems = (action.data?.items || []).map(item => ({
        id: item.id || '',
        title: item.title || '',
        description: item.description || '',
        'on-click-action': item['on-click-action'] || null
      }));
      
      return {
        type: 'NavigationList',
        id: cleanedId,
        items: navListItems
      };
    
    case 'chips_selector':
      // ChipsSelector 使用 data-source（內聯數組），不是 options
      // 根據官方文檔，data-source 是一個直接內聯的數組，不是 ${data.xxx} 格式
      const chipsOptions = (action.data?.options || []).map(opt => ({
        id: opt.id || opt.value || '',
        title: opt.title || opt.text || ''
      }));
      
      // ChipsSelector 使用 on-select-action，類似於 CheckboxGroup 和 RadioButtonsGroup
      let chipsSelectAction = null;
      if (action.action) {
        if (action.action.type === 'submit' || action.action.type === 'navigate') {
          chipsSelectAction = {
            name: action.action.type === 'submit' ? 'update_data' : 'navigate',
            payload: action.action.payload || {}
          };
          if (action.action.type === 'navigate' && action.action.next) {
            chipsSelectAction.next = processNextValue(action.action.next);
          }
        } else if (action.action.type === 'url') {
          chipsSelectAction = {
            name: 'open_url',
            url: action.action.endpoint || action.action.url || '',
            payload: action.action.payload || {}
          };
        }
      }
      
      const chipsSelectorComponent = {
        type: 'ChipsSelector',
        name: cleanId(action.name || action.id || `chips_selector_${Date.now()}`),
        label: action.title || '小標籤選擇器',
        'data-source': chipsOptions, // 直接使用靜態數組，格式：[{ "id": "...", "title": "..." }]
        required: action.data?.required || false
      };
      
      // 添加可選字段
      if (action.data?.max_selected_items !== undefined && action.data?.max_selected_items !== null) {
        chipsSelectorComponent['max-selected-items'] = action.data.max_selected_items;
      }
      if (action.data?.description) {
        chipsSelectorComponent.description = action.data.description;
      }
      
      // 添加 on-select-action（如果存在）
      if (chipsSelectAction) {
        chipsSelectorComponent['on-select-action'] = chipsSelectAction;
      }
      
      return chipsSelectorComponent;
    
    case 'image_carousel':
      // 根據官方文檔：ImageCarousel 不需要 id，只需要 images, aspect-ratio, scale-type
      // 參考：https://developers.facebook.com/docs/whatsapp/flows/reference/components#image_carousel
      const carouselImages = (action.data?.images || []).map(img => ({
        src: img.src || '',
        'alt-text': img['alt-text'] || img.alt || ''
      }));
      
      const imageCarouselComponent = {
        type: 'ImageCarousel',
        images: carouselImages
      };
      
      // 添加可選屬性
      if (action.data?.['aspect-ratio']) {
        imageCarouselComponent['aspect-ratio'] = action.data['aspect-ratio'];
      }
      if (action.data?.['scale-type']) {
        imageCarouselComponent['scale-type'] = action.data['scale-type'];
      }
      
      return imageCarouselComponent;
    
    case 'calendar_picker':
      // CalendarPicker 類似 DatePicker，使用 name 而不是 id，使用 on-select-action
      const calendarPickerName = cleanId(action.name || action.id || `calendar_picker_${Date.now()}`);
      
      let calendarSelectAction = null;
      if (action.action) {
        if (action.action.type === 'submit' || action.action.type === 'navigate') {
          calendarSelectAction = {
            name: action.action.type === 'submit' ? 'data_exchange' : 'navigate',
            payload: action.action.payload || {}
          };
          if (action.action.type === 'navigate' && action.action.next) {
            const nextValue = processNextValue(action.action.next);
            if (nextValue) {
              calendarSelectAction.next = {
                name: nextValue,
                type: 'screen'
              };
            }
          }
        } else {
          // 如果 action.action.type 不是 submit 或 navigate，也使用 data_exchange
          // 因為 CalendarPicker 通常需要 data_exchange 來觸發服務器請求
          calendarSelectAction = {
            name: 'data_exchange',
            payload: action.action.payload || {}
          };
        }
      } else {
        // 默認使用 data_exchange（根據官方文檔，CalendarPicker 使用 data_exchange 時需要 data_api_version）
        calendarSelectAction = {
          name: 'data_exchange',
          payload: {}
        };
      }
      
      const calendarPickerComponent = {
        type: 'CalendarPicker',
        name: calendarPickerName,
        label: action.title || '日曆選擇',
        required: action.data?.required || false
      };
      
      if (action.data?.enabled !== undefined) {
        calendarPickerComponent.enabled = action.data.enabled;
      }
      if (action.data?.visible !== undefined) {
        calendarPickerComponent.visible = action.data.visible;
      }
      if (action.data?.description && action.data.description.trim() !== '') {
        calendarPickerComponent.description = action.data.description;
      }
      if (action.data?.error_message) {
        if (typeof action.data.error_message === 'object') {
          calendarPickerComponent['error-message'] = action.data.error_message;
        } else if (typeof action.data.error_message === 'string' && action.data.error_message.trim() !== '') {
          calendarPickerComponent['error-message'] = { text: action.data.error_message };
        }
      }
      
      calendarPickerComponent['on-select-action'] = calendarSelectAction;
      
      return calendarPickerComponent;
    
    default:
      console.warn(`未實現的組件類型: ${action.type}`);
      return null;
  }
};

/**
 * 將編輯器數據轉換為 Meta Flow JSON
 * 嚴格按照官方文檔格式：https://developers.facebook.com/docs/whatsapp/flows/reference/flowjson
 */
export const generateMetaFlowJson = (flowData, t = null) => {
  try {
    const { name, categories, screens } = flowData;
    
    if (!name) {
      throw new Error('Flow 必須包含 name');
    }
    
    const screensArray = screens || [];
    const categoriesArray = categories && categories.length > 0 ? categories : ['LEAD_GENERATION'];
    
    // 根據 Meta API 限制，檢查所有 PhotoPicker 和 DocumentPicker 組件
    // 限制 1: 總文件數量不能超過 10
    // 限制 2: 總文件大小不能超過 102400 KB
    // 如果沒有設置，默認值是 30 個文件和 25600 KB 每個文件
    let totalMaxFiles = 0; // 所有 PhotoPicker 和 DocumentPicker 的 max-uploaded 總和
    let totalMaxSizeKB = 0; // 所有 PhotoPicker 和 DocumentPicker 的 max-file-size-kb 總和
    
    // 先遍歷所有屏幕，收集所有 PhotoPicker 和 DocumentPicker 組件
        screensArray.forEach((screen, screenIndex) => {
      if (screen.data?.actions && screen.data.actions.length > 0) {
        screen.data.actions.forEach(action => {
          if (action.type === 'photo_picker') {
            const maxUploaded = action.data?.max_uploaded_photos ?? 3; // 默認值：3 張照片
            const maxFileSize = action.data?.max_file_size_kb ?? 25600; // Meta API 限制：單個組件的 max-file-size-kb 最大值為 25600 KB，所有組件的總和不能超過 102400 KB
            totalMaxFiles += maxUploaded;
            totalMaxSizeKB += maxFileSize;
          } else if (action.type === 'document_picker') {
            const maxUploaded = action.data?.max_uploaded_documents ?? 10; // Meta API 限制：所有組件的總文件數量不能超過 10
            const maxFileSize = action.data?.max_file_size_kb ?? 25600; // Meta API 限制：單個組件的 max-file-size-kb 最大值為 25600 KB，所有組件的總和不能超過 102400 KB
            totalMaxFiles += maxUploaded;
            totalMaxSizeKB += maxFileSize;
          }
        });
      }
    });
    
    // 檢查限制並給出警告
    const MAX_TOTAL_FILES = 10;
    const MAX_TOTAL_SIZE_KB = 102400;
    
    if (totalMaxFiles > MAX_TOTAL_FILES) {
      console.warn(`⚠️ [Meta Flow 限制警告] 所有 PhotoPicker 和 DocumentPicker 的總文件數量 (${totalMaxFiles}) 超過限制 (${MAX_TOTAL_FILES})。Meta API 可能會拒絕此 Flow。`);
      console.warn(`   建議：調整各組件的 max-uploaded-photos 或 max-uploaded-documents 值，使總和不超過 ${MAX_TOTAL_FILES}。`);
    }
    
    if (totalMaxSizeKB > MAX_TOTAL_SIZE_KB) {
      console.warn(`⚠️ [Meta Flow 限制警告] 所有 PhotoPicker 和 DocumentPicker 的總文件大小 (${totalMaxSizeKB} KB) 超過限制 (${MAX_TOTAL_SIZE_KB} KB)。Meta API 可能會拒絕此 Flow。`);
      console.warn(`   建議：調整各組件的 max-file-size-kb 值，使總和不超過 ${MAX_TOTAL_SIZE_KB} KB。`);
    }
    
    // 根據官方 Flow JSON 格式：https://developers.facebook.com/docs/whatsapp/flows/reference/flowjson
    // 先檢查是否有任何屏幕使用 data_exchange action，如果有則需要在頂層添加 data_api_version
    let hasDataExchangeInAnyScreen = false;
    
    const screenIdMap = createScreenIdMap(screensArray);

    const metaFlowJson = {
      version: "7.3",
      // data_api_version 和 routing_model 會在後面根據需要添加
      screens: screensArray.map((screen, screenIndex) => {
        const children = [];
        
        // 1. TextHeading (標題) - 從 screen.data.header
        if (screen.data?.header?.text) {
          children.push({
            type: 'TextHeading',
            text: screen.data.header.text
          });
        }
        
        // 2. TextBody (正文) - 必須有，從 screen.data.body
        let bodyText = screen.data?.body?.text || '';
        // 如果 bodyText 是硬編碼的默認值，且提供了翻譯函數，則使用翻譯
        if (t && bodyText === '請輸入內容') {
          bodyText = t('metaFlowBuilder.screenEditor.defaultValues.bodyText');
        }
        children.push({
          type: 'TextBody',
          text: bodyText
        });
        
        // 3. 處理所有 actions（組件庫中的組件）
        // 同時收集 Dropdown 和 CheckboxGroup 組件的選項，以便更新到 data 模型的 __example__ 中
        const dynamicOptionsMap = new Map(); // dataSourceName -> options array
        // Meta API 要求表單元件 name 不可重複，在此做唯一化
        const formComponentTypes = ['TextInput', 'TextArea', 'DatePicker', 'CalendarPicker', 'TimePicker', 'Dropdown', 'CheckboxGroup', 'RadioButtonsGroup', 'ChipsSelector', 'PhotoPicker', 'DocumentPicker'];
        const usedFormNames = new Set();
        
        const initValues = {};
        if (screen.data?.actions && screen.data.actions.length > 0) {
          screen.data.actions.forEach(action => {
            const component = convertActionToComponent(action);
            if (component) {
              // 表單元件 name 唯一化，避免 Meta 報錯 "Duplicate name found for Form components"
              if (component.name && formComponentTypes.includes(component.type)) {
                let uniqueName = component.name;
                if (usedFormNames.has(uniqueName)) {
                  let suffix = 1;
                  while (usedFormNames.has(uniqueName + '_' + suffix)) suffix++;
                  uniqueName = component.name + '_' + suffix;
                  component.name = uniqueName;
                }
                usedFormNames.add(component.name);
              }
              if (action.data?.default_value && component.name) {
                initValues[component.name] = action.data.default_value;
              }
              // 調試日誌：檢查 DatePicker 和 CalendarPicker 的轉換結果
              if (action.type === 'date_picker' || action.type === 'calendar_picker') {
                console.log(`🔄 [generateMetaFlowJson] 轉換 ${action.type}:`, {
                  originalAction: action,
                  convertedComponent: component,
                  hasOnSelectAction: !!component['on-select-action'],
                  onSelectActionName: component['on-select-action']?.name
                });
              }
              children.push(component);
              
              // 注意：Dropdown、CheckboxGroup、RadioButtonsGroup 現在直接使用靜態 data-source 數組
              // 不再需要更新 dataModel 的 __example__，因為 data-source 已經是靜態數組了
              // 這段代碼保留用於向後兼容（如果 data-source 是 ${data.xxx} 格式）
              if (component.type === 'Dropdown' || component.type === 'CheckboxGroup' || component.type === 'RadioButtonsGroup') {
                const dataSource = component['data-source'];
                // 如果 data-source 是數組（靜態數據），則不需要處理
                if (Array.isArray(dataSource)) {
                  // 已經是靜態數組，不需要更新 dataModel
                  // 跳過後續處理
                } else {
                  // 如果是 ${data.xxx} 格式（向後兼容），則保留原有邏輯
                  const dataSourceName = extractDataSourceName(dataSource);
                  if (dataSourceName) {
                    // 優先使用 action.data.options（用戶在編輯器中編輯的選項）
                    if (action.data?.options && Array.isArray(action.data.options) && action.data.options.length > 0) {
                      // 將選項轉換為 __example__ 格式：{ id: string, title: string }
                      const exampleOptions = action.data.options.map(opt => ({
                        id: opt.id || opt.value || `option_${Date.now()}_${Math.random()}`,
                        title: opt.title || opt.text || opt.label || ''
                      }));
                      dynamicOptionsMap.set(dataSourceName, exampleOptions);
                    } else if (screen.data?.dataModel?.[dataSourceName]?.__example__ && 
                               Array.isArray(screen.data.dataModel[dataSourceName].__example__) &&
                               screen.data.dataModel[dataSourceName].__example__.length > 0) {
                      // 如果 action.data.options 不存在或為空，但 dataModel 中有 __example__，使用它
                      dynamicOptionsMap.set(dataSourceName, screen.data.dataModel[dataSourceName].__example__);
                    }
                  }
                }
              }
            } else {
              console.warn(`跳過不支持的組件類型: ${action.type}`);
            }
          });
        }
        
        // 4. Footer - 必須有，從 screen.data.footer
        // Footer 是必填項，如果為空則使用默認值
        let footerText = screen.data?.footer?.text || '提交';
        // 如果 footerText 是硬編碼的默認值，且提供了翻譯函數，則使用翻譯
        if (t && footerText === '提交') {
          footerText = t('metaFlowBuilder.screenEditor.defaultValues.footerText');
        }
        
        // 收集所有輸入組件的 name，用於生成 complete payload
        // 根據 Meta 文檔，payload 中應該包含所有要提交的字段，格式：{ "fieldName": "${form.fieldName}" }
        const inputComponentNames = [];
        children.forEach(child => {
          // 檢查是否是輸入組件（有 name 字段的組件）
          if (child.name) {
            // 跳過非輸入組件（如 TextHeading, TextBody, Image 等）
            const inputComponentTypes = [
              'TextInput', 'TextArea', 'DatePicker', 'CalendarPicker', 'TimePicker',
              'Dropdown', 'CheckboxGroup', 'RadioButtonsGroup', 'ChipsSelector',
              'PhotoPicker', 'DocumentPicker'
            ];
            if (inputComponentTypes.includes(child.type)) {
              inputComponentNames.push(child.name);
            }
          }
        });
        
        // 生成 complete payload
        const completePayload = {};
        inputComponentNames.forEach(fieldName => {
          // 根據 Meta 文檔，payload 格式：{ "fieldName": "${form.fieldName}" }
          completePayload[fieldName] = `\${form.${fieldName}}`;
        });
        
        console.log(`📋 [generateMetaFlowJson] Footer complete payload:`, {
          inputComponentNames,
          completePayload,
          payloadKeys: Object.keys(completePayload)
        });
        
        children.push({
          type: 'Footer',
          label: footerText,
          'on-click-action': {
            name: 'complete',
            payload: completePayload
          }
        });
        
        // 構建 screen 對象
        const originalScreenId = screen.id || `screen_${screenIndex}`;
        const screenId = screenIdMap.get(originalScreenId) || cleanScreenId(originalScreenId) || 'screen';

        // 將 navigate 目標同步映射到新的 screen id，避免跳轉目標失配
        children.forEach(child => {
          const normalizeNavigateNext = (actionObj) => {
            if (!actionObj || actionObj.name !== 'navigate' || !actionObj.next?.name) return;
            const mappedName = screenIdMap.get(actionObj.next.name) || cleanScreenId(actionObj.next.name) || actionObj.next.name;
            actionObj.next = {
              ...actionObj.next,
              name: mappedName,
              type: 'screen'
            };
          };
          normalizeNavigateNext(child['on-click-action']);
          normalizeNavigateNext(child['on-select-action']);
        });
        
        // 檢查是否有任何組件使用 data_exchange action
        // 根據官方文檔：當使用 data_exchange action 時，必須在屏幕級別添加 data_api_version
        const hasDataExchange = children.some(child => {
          // 檢查 on-click-action 或 on-select-action 是否為 data_exchange
          const onClickAction = child['on-click-action'];
          const onSelectAction = child['on-select-action'];
          const isDataExchange = (onClickAction && onClickAction.name === 'data_exchange') ||
                                 (onSelectAction && onSelectAction.name === 'data_exchange');
          
          // 調試日誌：檢查 DatePicker 和 CalendarPicker
          if (child.type === 'DatePicker' || child.type === 'CalendarPicker') {
            console.log(`🔍 [generateMetaFlowJson] 檢查 ${child.type}:`, {
              type: child.type,
              hasOnClickAction: !!onClickAction,
              onClickActionName: onClickAction?.name,
              hasOnSelectAction: !!onSelectAction,
              onSelectActionName: onSelectAction?.name,
              isDataExchange: isDataExchange,
              fullChild: JSON.stringify(child, null, 2)
            });
          }
          
          return isDataExchange;
        });
        
        console.log(`📊 [generateMetaFlowJson] 檢測結果:`, {
          hasDataExchange: hasDataExchange,
          childrenCount: children.length,
          childrenTypes: children.map(c => c.type)
        });
        
        // 使用規範配置檢查是否需要 terminal screen
        const componentsRequiringTerminal = getComponentsRequiringTerminal();
        const hasTerminalComponent = children.some(child => {
          const spec = getComponentSpec(child.type);
          return spec && spec.requiresTerminal;
        });
        
        // 檢查是否有 Footer 使用 complete action，如果有則需要設置 terminal: true
        const hasCompleteFooter = children.some(child => 
          child.type === 'Footer' && 
          child['on-click-action'] && 
          child['on-click-action'].name === 'complete'
        );
        
        // 使用規範配置檢查是否需要動態數據
        const needsDataModel = children.some(child => {
          const spec = getComponentSpec(child.type);
          if (spec && spec.requiresDataModel && child['data-source']) {
            return true;
          }
          return false;
        });
        
        // 先構建基本 screen 對象（不包含 data，因為 data 需要在 layout 之後）
        // 重要：字段順序必須與 Meta API 要求一致：id, title, layout, terminal, data_api_version, data
        let screenTitle = screen.title || '';
        // 如果 screenTitle 是硬編碼的默認值，且提供了翻譯函數，則使用翻譯
        if (t && screenTitle === '新 Screen') {
          screenTitle = t('metaFlowBuilder.page.defaultScreenTitle');
        }
        const screenObj = {
          id: screenId,
          title: screenTitle,
          layout: {
            type: 'SingleColumnLayout',
            children: children
          }
        };
        
        // 檢查是否有組件使用動態變量 (DS_/MC_)
        // 當 Flow 使用 data-source="${data.DS_...}" 或 "${data.MC_...}" 時，Meta 會在進入 screen 時自動發送 INIT 請求
        // 我們不需要手動添加 on-init-action（Meta 不支持此屬性）
        // 後端會處理 INIT 請求並返回數據，確保 dropdown 在打開時就能看到選項
        const hasDynamicDataSource = children.some(child => {
          const dataSource = child['data-source'];
          return dataSource && typeof dataSource === 'string'
            && (dataSource.startsWith('${data.DS_') || dataSource.startsWith('${data.MC_'));
        });
        
        // 如果有 data_exchange action，標記需要在頂層添加 data_api_version
        // 根據官方文檔：https://developers.facebook.com/docs/whatsapp/flows/reference/components#dp
        // 注意：data_api_version 應該在 Flow JSON 的頂層，而不是屏幕級別
        if (hasDataExchange || hasDynamicDataSource) {
          hasDataExchangeInAnyScreen = true;
          console.log(`✅ [generateMetaFlowJson] 檢測到 data_exchange action 或動態 data-source 在屏幕 ${screenId}，將在頂層添加 data_api_version`);
        }
        
        // 如果有 complete action 的 Footer 或其他需要 terminal 的組件，設置 terminal: true
        // 注意：根據用戶測試，success 字段不是必需的，所以移除它
        if (hasCompleteFooter || hasTerminalComponent) {
          screenObj.terminal = true;
        }
        
        // 如果需要動態數據，添加 data 模型定義
        // 注意：screen.data 只應包含數據模型定義（如 dropdown_select、checkbox_checkbox 等），
        // 不應包含 body、footer、header、actions 等編輯器內部字段
        // 重要：data 字段必須在 layout 和 terminal 之後，以匹配 Meta API 的格式要求
        
        // 首先收集所有使用靜態 data-source 數組的組件名稱
        // 這些組件不需要在 screen.data 中定義數據模型
        const staticDataSourceComponentNames = new Set();
        children.forEach(child => {
          if (child['data-source'] && Array.isArray(child['data-source'])) {
            // 組件使用靜態 data-source 數組，不需要數據模型定義
            // 記錄組件名稱，以便後續過濾掉對應的數據模型定義
            if (child.name) {
              staticDataSourceComponentNames.add(child.name);
            }
          }
        });
        
        if (needsDataModel || screen.data?.dataModel) {
          // 從 dataModel 中過濾掉編輯器內部字段，只保留數據模型定義
          const dataModel = screen.data?.dataModel || {};
          const filteredDataModel = {};
          
          // 只保留數據模型定義（不包含 body、footer、header、actions）
          // 同時過濾掉使用靜態 data-source 數組的組件對應的數據模型定義
          Object.keys(dataModel).forEach(key => {
            if (key !== 'body' && key !== 'footer' && key !== 'header' && key !== 'actions') {
              // 檢查這個數據模型是否對應於使用靜態 data-source 的組件
              // 數據模型名稱格式通常是：dropdown_<component_name> 或 checkbox_<component_name> 等
              // 例如：dropdown_select 對應於 name="select" 的 Dropdown 組件
              let shouldInclude = true;
              
              // 檢查是否匹配任何使用靜態 data-source 的組件名稱
              for (const componentName of staticDataSourceComponentNames) {
                // 數據模型名稱可能包含組件類型前綴（如 dropdown_、checkbox_、radio_）
                // 檢查 key 是否匹配以下模式：
                // 1. key === componentName（完全匹配）
                // 2. key === `${type}_${componentName}`（例如：dropdown_select）
                // 3. key.endsWith(`_${componentName}`)（例如：some_prefix_select）
                const prefixes = ['dropdown_', 'checkbox_', 'radio_'];
                const matches = prefixes.some(prefix => key === `${prefix}${componentName}`) ||
                               key === componentName ||
                               key.endsWith(`_${componentName}`);
                
                if (matches) {
                  shouldInclude = false;
                  console.log(`🔍 [generateMetaFlowJson] 過濾掉數據模型 "${key}"，因為組件 "${componentName}" 使用靜態 data-source 數組`);
                  break;
                }
              }
              
              if (shouldInclude) {
                filteredDataModel[key] = dataModel[key];
              }
            }
          });
          
          // 在 layout 和 terminal 之後添加 data 字段
          // 只有在 filteredDataModel 不為空時才添加
          if (Object.keys(filteredDataModel).length > 0) {
            screenObj.data = filteredDataModel;
          }
          
          // 使用規範配置為所有需要數據模型的組件添加數據定義
          children.forEach(child => {
            const spec = getComponentSpec(child.type);
            // 只處理需要數據模型且使用動態 data-source（${data.xxx} 格式）的組件
            if (spec && spec.requiresDataModel && child['data-source']) {
              // 如果 data-source 是數組（靜態數據），跳過
              if (Array.isArray(child['data-source'])) {
                return; // 跳過，不需要數據模型定義
              }
              
              const dataSourceName = extractDataSourceName(child['data-source']);
              if (dataSourceName) {
                // 如果已經存在數據模型，使用現有的；否則創建新的
                if (!screenObj.data) {
                  screenObj.data = {};
                }
                if (!screenObj.data[dataSourceName]) {
                  // 使用規範配置中的數據模型模板
                  const dataModel = generateDataModel(dataSourceName, child.type);
                  if (dataModel) {
                    screenObj.data[dataSourceName] = dataModel;
                  }
                }
                
                // 如果有編輯的選項，更新 __example__
                // 如果沒有編輯的選項，但已存在 __example__，保留它
                if (dynamicOptionsMap.has(dataSourceName)) {
                  // 用戶在編輯器中編輯了選項，使用新的選項
                  const exampleOptions = dynamicOptionsMap.get(dataSourceName);
                  if (screenObj.data[dataSourceName]) {
                    screenObj.data[dataSourceName].__example__ = exampleOptions;
                  } else {
                    // 如果數據模型不存在，創建一個
                    screenObj.data[dataSourceName] = {
                      type: 'array',
                      items: {
                        type: 'object',
                        properties: {
                          id: { type: 'string' },
                          title: { type: 'string' }
                        }
                      },
                      __example__: exampleOptions
                    };
                  }
                } else {
                  // 如果沒有編輯的選項，保留已存在的 __example__
                  // 如果數據模型中沒有 __example__，確保至少有一個空數組（避免 Meta API 驗證錯誤）
                  if (screenObj.data[dataSourceName] && !screenObj.data[dataSourceName].__example__) {
                    screenObj.data[dataSourceName].__example__ = [];
                  }
                  // 如果 screenObj.data[dataSourceName].__example__ 已存在（即使是空數組），則保留它（不需要做任何操作）
                }
              }
            }
          });
        }
        
        const hasInitValues = Object.keys(initValues).length > 0;
        if (hasInitValues) {
          const displayTypes = new Set(['TextHeading', 'TextBody', 'Image', 'RichText']);
          const displayChildren = children.filter(c => displayTypes.has(c.type));
          const formChildren = children.filter(c => !displayTypes.has(c.type));
          const formComponent = {
            type: 'Form',
            name: 'form',
            'init-values': initValues,
            children: formChildren
          };
          children.length = 0;
          displayChildren.forEach(c => children.push(c));
          children.push(formComponent);
        }
        
        return screenObj;
      })
    };
    
    // 檢查是否有使用動態變量 (DS_/MC_) 並收集它們
    const dynamicDatasetVariables = new Set();
    const collectDynamicVars = (children) => {
      children.forEach(child => {
        if (
          child['data-source'] &&
          typeof child['data-source'] === 'string' &&
          (child['data-source'].startsWith('${data.DS_') || child['data-source'].startsWith('${data.MC_'))
        ) {
          const varName = child['data-source'].replace('${data.', '').replace('}', '');
          dynamicDatasetVariables.add(varName);
        }
        if (child.type === 'Form' && child.children) {
          collectDynamicVars(child.children);
        }
      });
    };
    metaFlowJson.screens.forEach(screen => {
      if (screen.layout && screen.layout.children) {
        collectDynamicVars(screen.layout.children);
      }
    });

    // 如果有任何屏幕使用 data_exchange action 或者使用了動態 Dataset 變量，在頂層添加 data_api_version 和 routing_model
    // 根據官方文檔和例子：字段順序應該是 version, data_api_version, routing_model, screens
    // 參考：https://developers.facebook.com/docs/whatsapp/flows/reference/components#dp
    if (hasDataExchangeInAnyScreen || dynamicDatasetVariables.size > 0) {
      // 構建端點 URI（需要從當前環境獲取，這裡先使用相對路徑，後端會處理）
      // 注意：endpoint_uri 應該在 Flow JSON 中指定，但實際的完整 URL 需要後端根據 companyId 構建
      // 這裡先不添加 endpoint_uri，讓後端在發送到 Meta 之前動態添加
      
      // 重新構建 metaFlowJson，確保字段順序正確：version, data_api_version, routing_model, screens
      const orderedMetaFlowJson = {
        version: metaFlowJson.version,
        data_api_version: "3.0",
        routing_model: {},
        screens: metaFlowJson.screens.map(screen => {
          // 確保 screen.data 包含所有動態變量的定義
          if (dynamicDatasetVariables.size > 0) {
            if (!screen.data) screen.data = {};
            
            dynamicDatasetVariables.forEach(varName => {
              if (!screen.data[varName]) {
                // 根據變量名判斷組件類型（雖然這裡無法準確判斷，但 Dropdown/Checkbox/Radio 的數據模型結構是一樣的）
                // 默認使用 Dropdown 的數據模型結構
                screen.data[varName] = {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      id: { type: 'string' },
                      title: { type: 'string' }
                    }
                  },
                  __example__: []
                };
              }
            });
          }
          return screen;
        })
      };
      console.log(`✅ [generateMetaFlowJson] 已在頂層添加 data_api_version: "3.0" 和 routing_model`);
      return orderedMetaFlowJson;
    }
    
    return metaFlowJson;
  } catch (error) {
    console.error('生成 Meta Flow JSON 失敗:', error);
    throw error;
  }
};

/**
 * 將 Meta API 返回的 layout 格式轉換為編輯器使用的 data 格式
 */
const convertLayoutToDataFormat = (screen) => {
  const data = {
    body: { type: 'body', text: '' },
    footer: { type: 'footer', text: '提交' }, // Footer 是必填項，設置默認值
    header: { type: 'header', format: 'TEXT', text: '' }, // header 不能為 null，必須是對象
    actions: []
  };

  if (screen.layout && screen.layout.children) {
    screen.layout.children.forEach(child => {
      switch (child.type) {
        case 'TextHeading':
          // header 必須是對象，不能為 null
          data.header = {
            type: 'header',
            format: 'TEXT',
            text: child.text || ''
          };
          break;
        
        case 'TextBody':
          if (child.text) {
            data.body.text = child.text;
          }
          break;
        
        case 'RichText':
        case 'rich_text':
          // RichText 使用 text 數組，支持 Markdown 語法
          if (child.text) {
            const textArray = Array.isArray(child.text) ? child.text : [child.text];
            data.actions.push({
              type: 'rich_text',
              id: child.id || `rich_text_${Date.now()}`,
              title: '富文本顯示',
              data: {
                text: textArray
              }
            });
          }
          break;
        
        case 'Footer':
          // Footer 是必填項，如果沒有 label 則使用默認值
          data.footer.text = child.label || '提交';
          if (child['on-click-action']) {
            data.footer.action = {
              type: child['on-click-action'].name === 'complete' ? 'submit' : 'navigate',
              payload: child['on-click-action'].payload || {},
              next: child['on-click-action'].next?.name || ''
            };
          }
          break;
        
        case 'Button':
        case 'button':
          data.actions.push({
            type: 'button',
            id: child.id || `button_${Date.now()}`,
            title: child.label || child.text || '按鈕',
            action: child['on-click-action'] ? {
              type: child['on-click-action'].name === 'complete' || child['on-click-action'].name === 'data_exchange' ? 'submit' : 
                    (child['on-click-action'].name === 'navigate' ? 'navigate' : 'url'),
              next: child['on-click-action'].next?.name || '',
              payload: child['on-click-action'].payload || {},
              method: 'GET',
              endpoint: child['on-click-action'].url || ''
            } : {
              type: 'navigate',
              next: '',
              payload: {},
              method: 'GET',
              endpoint: ''
            },
            data: null
          });
          break;
        
        case 'Form':
          if (child.children && Array.isArray(child.children)) {
            const formInitValues = child['init-values'] || {};
            child.children.forEach(formChild => {
              const tempScreen = { layout: { children: [formChild] } };
              const converted = convertLayoutToDataFormat(tempScreen);
              const convertedData = converted?.data;
              if (convertedData?.footer?.text && convertedData.footer.text !== '提交') {
                data.footer = convertedData.footer;
              }
              (convertedData?.actions || []).forEach(act => {
                if (act.name && formInitValues[act.name]) {
                  if (!act.data) act.data = {};
                  act.data.default_value = formInitValues[act.name];
                }
                data.actions.push(act);
              });
            });
          }
          break;

        case 'Input':
        case 'TextInput':
        case 'text_input':
          data.actions.push({
            type: 'text_input',
            name: child.name || child.id || `text_input_${Date.now()}`,
            title: child.label || '文字輸入',
            data: {
              input_type: child['input-type'] || child.input_type || 'text',
              required: child.required || false,
              pattern: child.pattern || '',
              helper_text: child['helper-text'] || child.helper_text || ''
            },
            action: {
              type: 'submit',
              payload: {}
            }
          });
          break;
        
        case 'TextArea':
        case 'text_area':
          data.actions.push({
            type: 'text_area',
            name: child.name || child.id || `text_area_${Date.now()}`,
            title: child.label || '多行文字',
            data: {
              required: child.required || false,
              max_length: child['max-length'] || child.max_length || '600',
              helper_text: child['helper-text'] || child.helper_text || ''
            },
            action: {
              type: 'submit',
              payload: {}
            }
          });
          break;
        
        case 'Dropdown':
        case 'Select':
        case 'select':
          // Dropdown 使用 name 而不是 id，使用 on-select-action 而不是 on-click-action
          const dataSource = child['data-source'] || [];
          let dropdownOptions = [];
          let dropdownDatasetConfig = null;
          
          // 檢查是否是 Dataset 配置 (格式: ${data.DS_encodedString})
          if (typeof dataSource === 'string' && dataSource.startsWith('${data.DS_')) {
             const encodedConfig = dataSource.substring(10, dataSource.length - 1);
             dropdownDatasetConfig = decodeDatasetConfig(encodedConfig);
          }
          let dropdownMetaCollectionConfig = null;
          if (typeof dataSource === 'string' && dataSource.startsWith('${data.MC_')) {
             const encodedConfig = dataSource.substring(10, dataSource.length - 1);
             dropdownMetaCollectionConfig = decodeMetaCollectionConfig(encodedConfig);
          }
          
          // 如果不是 Dataset 配置，則嘗試解析為靜態選項
          if (!dropdownDatasetConfig && !dropdownMetaCollectionConfig) {
              // 如果 data-source 是數組（靜態數據），直接使用
              if (Array.isArray(dataSource)) {
                dropdownOptions = dataSource.map(opt => ({
                  id: opt.id || opt.value || '',
                  title: opt.title || opt.text || ''
                }));
              } 
              // 如果是字符串格式（向後兼容），嘗試從 screen.data 的 __example__ 中讀取
              else if (typeof dataSource === 'string') {
                const dataSourceName = extractDataSourceName(dataSource);
                if (dataSourceName && screen.data && screen.data[dataSourceName] && screen.data[dataSourceName].__example__) {
                  dropdownOptions = screen.data[dataSourceName].__example__.map(example => ({
                    id: example.id || '',
                    title: example.title || ''
                  }));
                }
              }
          }
          
          const dropdownData = {
              options: dropdownOptions
          };
          
          if (dropdownDatasetConfig) {
              dropdownData.optionsSource = 'dataset';
              dropdownData.datasetId = dropdownDatasetConfig.datasetId;
              dropdownData.idField = dropdownDatasetConfig.idField;
              dropdownData.titleField = dropdownDatasetConfig.titleField;
          } else if (dropdownMetaCollectionConfig) {
              dropdownData.optionsSource = 'meta_collection';
              dropdownData.metaCollectionId = dropdownMetaCollectionConfig.collectionId;
              dropdownData.metaCollectionLimit = dropdownMetaCollectionConfig.limit;
          } else {
              dropdownData.optionsSource = 'manual';
          }
          
          data.actions.push({
            type: 'select',
            name: child.name || child.id || `select_${Date.now()}`,
            title: child.label || '下拉選擇',
            action: child['on-select-action'] || child['on-click-action'] ? {
              type: (child['on-select-action'] || child['on-click-action']).name === 'complete' || (child['on-select-action'] || child['on-click-action']).name === 'data_exchange' ? 'submit' : 
                    ((child['on-select-action'] || child['on-click-action']).name === 'navigate' ? 'navigate' : 'url'),
              next: (child['on-select-action'] || child['on-click-action']).next?.name || '',
              payload: (child['on-select-action'] || child['on-click-action']).payload || {},
              method: 'GET',
              endpoint: (child['on-select-action'] || child['on-click-action']).url || ''
            } : {
              type: 'navigate',
              next: '',
              payload: {},
              method: 'GET',
              endpoint: ''
            },
            data: dropdownData
          });
          break;

        case 'DatePicker':
        case 'date_picker':
          // DatePicker 使用 name 而不是 id，使用 on-select-action 而不是 on-click-action
          const datePickerData = {
            required: child.required || false
          };
          
          // 添加可選屬性
          if (child.enabled !== undefined) {
            datePickerData.enabled = child.enabled;
          }
          if (child.visible !== undefined) {
            datePickerData.visible = child.visible;
          }
          if (child.description && child.description.trim() !== '') {
            datePickerData.description = child.description;
          }
          if (child['error-message']) {
            if (typeof child['error-message'] === 'object') {
              datePickerData.error_message = child['error-message'];
            } else if (typeof child['error-message'] === 'string' && child['error-message'].trim() !== '') {
              datePickerData.error_message = { text: child['error-message'] };
            }
          }
          
          data.actions.push({
            type: 'date_picker',
            name: child.name || `date_picker_${Date.now()}`,
            title: child.label || '日期選擇',
            action: child['on-select-action'] ? {
              type: child['on-select-action'].name === 'complete' || child['on-select-action'].name === 'data_exchange' ? 'submit' : 
                    (child['on-select-action'].name === 'navigate' ? 'navigate' : 'url'),
              next: child['on-select-action'].next?.name || '',
              payload: child['on-select-action'].payload || {},
              method: 'GET',
              endpoint: child['on-select-action'].url || ''
            } : {
              type: 'submit',
              next: '',
              payload: {},
              method: 'GET',
              endpoint: ''
            },
            data: datePickerData
          });
          break;

        case 'CheckboxGroup':
        case 'checkbox':
          // CheckboxGroup 使用 name 和 data-source 而不是 id 和 options
          const checkboxDataSource = child['data-source'] || [];
          let checkboxOptions = [];
          let checkboxDatasetConfig = null;
          
          // 檢查是否是 Dataset 配置
          if (typeof checkboxDataSource === 'string' && checkboxDataSource.startsWith('${data.DS_')) {
             const encodedConfig = checkboxDataSource.substring(10, checkboxDataSource.length - 1);
             checkboxDatasetConfig = decodeDatasetConfig(encodedConfig);
          }
          let checkboxMetaCollectionConfig = null;
          if (typeof checkboxDataSource === 'string' && checkboxDataSource.startsWith('${data.MC_')) {
             const encodedConfig = checkboxDataSource.substring(10, checkboxDataSource.length - 1);
             checkboxMetaCollectionConfig = decodeMetaCollectionConfig(encodedConfig);
          }
          
          if (!checkboxDatasetConfig && !checkboxMetaCollectionConfig) {
              // 如果 data-source 是數組（靜態數據），直接使用
              if (Array.isArray(checkboxDataSource)) {
                checkboxOptions = checkboxDataSource.map(opt => ({
                  id: opt.id || opt.value || '',
                  title: opt.title || opt.text || ''
                }));
              } 
              // 如果是字符串格式（向後兼容）
              else if (typeof checkboxDataSource === 'string') {
                const checkboxDataSourceName = extractDataSourceName(checkboxDataSource);
                if (checkboxDataSourceName && screen.data && screen.data[checkboxDataSourceName] && screen.data[checkboxDataSourceName].__example__) {
                  checkboxOptions = screen.data[checkboxDataSourceName].__example__.map(example => ({
                    id: example.id || '',
                    title: example.title || ''
                  }));
                }
              }
          }
          
          const checkboxData = {
              options: checkboxOptions
          };
          
          if (checkboxDatasetConfig) {
              checkboxData.optionsSource = 'dataset';
              checkboxData.datasetId = checkboxDatasetConfig.datasetId;
              checkboxData.idField = checkboxDatasetConfig.idField;
              checkboxData.titleField = checkboxDatasetConfig.titleField;
          } else if (checkboxMetaCollectionConfig) {
              checkboxData.optionsSource = 'meta_collection';
              checkboxData.metaCollectionId = checkboxMetaCollectionConfig.collectionId;
              checkboxData.metaCollectionLimit = checkboxMetaCollectionConfig.limit;
          } else {
              checkboxData.optionsSource = 'manual';
          }
          
          data.actions.push({
            type: 'checkbox',
            name: child.name || child.id || `checkbox_${Date.now()}`,
            title: child.label || '複選框組',
            action: child['on-select-action'] || child['on-click-action'] ? {
              type: (child['on-select-action'] || child['on-click-action']).name === 'complete' || (child['on-select-action'] || child['on-click-action']).name === 'data_exchange' ? 'submit' : 
                    ((child['on-select-action'] || child['on-click-action']).name === 'navigate' ? 'navigate' : 'url'),
              next: (child['on-select-action'] || child['on-click-action']).next?.name || '',
              payload: (child['on-select-action'] || child['on-click-action']).payload || {},
              method: 'GET',
              endpoint: (child['on-select-action'] || child['on-click-action']).url || ''
            } : {
              type: 'navigate',
              next: '',
              payload: {},
              method: 'GET',
              endpoint: ''
            },
            data: checkboxData
          });
          break;

        case 'RadioButtonsGroup':
        case 'radio':
          // RadioButtonsGroup 使用 name 和 data-source 而不是 id 和 options
          const radioDataSource = child['data-source'] || [];
          let radioOptions = [];
          let radioDatasetConfig = null;
          
          // 檢查是否是 Dataset 配置
          if (typeof radioDataSource === 'string' && radioDataSource.startsWith('${data.DS_')) {
             const encodedConfig = radioDataSource.substring(10, radioDataSource.length - 1);
             radioDatasetConfig = decodeDatasetConfig(encodedConfig);
          }
          let radioMetaCollectionConfig = null;
          if (typeof radioDataSource === 'string' && radioDataSource.startsWith('${data.MC_')) {
             const encodedConfig = radioDataSource.substring(10, radioDataSource.length - 1);
             radioMetaCollectionConfig = decodeMetaCollectionConfig(encodedConfig);
          }
          
          if (!radioDatasetConfig && !radioMetaCollectionConfig) {
              // 如果 data-source 是數組（靜態數據），直接使用
              if (Array.isArray(radioDataSource)) {
                radioOptions = radioDataSource.map(opt => ({
                  id: opt.id || opt.value || '',
                  title: opt.title || opt.text || ''
                }));
              } 
              // 如果是字符串格式（向後兼容）
              else if (typeof radioDataSource === 'string') {
                const radioDataSourceName = extractDataSourceName(radioDataSource);
                if (radioDataSourceName && screen.data && screen.data[radioDataSourceName] && screen.data[radioDataSourceName].__example__) {
                  radioOptions = screen.data[radioDataSourceName].__example__.map(example => ({
                    id: example.id || '',
                    title: example.title || ''
                  }));
                }
              }
          }
          
          const radioData = {
              options: radioOptions
          };
          
          if (radioDatasetConfig) {
              radioData.optionsSource = 'dataset';
              radioData.datasetId = radioDatasetConfig.datasetId;
              radioData.idField = radioDatasetConfig.idField;
              radioData.titleField = radioDatasetConfig.titleField;
          } else if (radioMetaCollectionConfig) {
              radioData.optionsSource = 'meta_collection';
              radioData.metaCollectionId = radioMetaCollectionConfig.collectionId;
              radioData.metaCollectionLimit = radioMetaCollectionConfig.limit;
          } else {
              radioData.optionsSource = 'manual';
          }
          
          data.actions.push({
            type: 'radio',
            name: child.name || child.id || `radio_${Date.now()}`,
            title: child.label || '單選框組',
            action: child['on-select-action'] || child['on-click-action'] ? {
              type: (child['on-select-action'] || child['on-click-action']).name === 'complete' || (child['on-select-action'] || child['on-click-action']).name === 'data_exchange' ? 'submit' : 
                    ((child['on-select-action'] || child['on-click-action']).name === 'navigate' ? 'navigate' : 'url'),
              next: (child['on-select-action'] || child['on-click-action']).next?.name || '',
              payload: (child['on-select-action'] || child['on-click-action']).payload || {},
              method: 'GET',
              endpoint: (child['on-select-action'] || child['on-click-action']).url || ''
            } : {
              type: 'navigate',
              next: '',
              payload: {},
              method: 'GET',
              endpoint: ''
            },
            data: radioData
          });
          break;

        case 'Image':
        case 'image':
          // 根據官方文檔，Image 組件使用 src 而不是 url，不支持 id、alt、on-click-action
          // src 應該是純 base64 字符串，不包含 data:image/xxx;base64, 前綴
          const imageSrcFromJson = child.src || child.url || '';
          // 如果 src 是純 base64（不包含 data: 前綴），構建完整 data URL 用於預覽
          const imageUrlForPreview = imageSrcFromJson.startsWith('data:image/') 
            ? imageSrcFromJson 
            : (imageSrcFromJson ? `data:image/png;base64,${imageSrcFromJson}` : '');
          
          data.actions.push({
            type: 'image',
            id: `image_${Date.now()}`, // 內部使用 id 用於 UI 識別
            title: '圖片',
            data: {
              url: imageUrlForPreview, // 完整 data URL 用於編輯器預覽
              src: imageSrcFromJson, // 純 base64 字符串（符合官方文檔）
              width: child.width,
              height: child.height
            },
            action: {
              type: 'navigate',
              next: '',
              payload: {},
              method: 'GET',
              endpoint: ''
            }
          });
          break;

        case 'Video':
        case 'video':
          data.actions.push({
            type: 'video',
            id: child.id || `video_${Date.now()}`,
            title: '視頻',
            action: child['on-click-action'] ? {
              type: child['on-click-action'].name === 'complete' || child['on-click-action'].name === 'data_exchange' ? 'submit' : 
                    (child['on-click-action'].name === 'navigate' ? 'navigate' : 'url'),
              next: child['on-click-action'].next?.name || '',
              payload: child['on-click-action'].payload || {},
              method: 'GET',
              endpoint: child['on-click-action'].url || ''
            } : {
              type: 'navigate',
              next: '',
              payload: {},
              method: 'GET',
              endpoint: ''
            },
            data: {
              url: child.url || '',
              thumbnail_url: child.thumbnail_url || ''
            }
          });
          break;

        case 'Document':
        case 'document':
          data.actions.push({
            type: 'document',
            id: child.id || `document_${Date.now()}`,
            title: child.filename || '文檔',
            action: child['on-click-action'] ? {
              type: child['on-click-action'].name === 'complete' || child['on-click-action'].name === 'data_exchange' ? 'submit' : 
                    (child['on-click-action'].name === 'navigate' ? 'navigate' : 'url'),
              next: child['on-click-action'].next?.name || '',
              payload: child['on-click-action'].payload || {},
              method: 'GET',
              endpoint: child['on-click-action'].url || ''
            } : {
              type: 'navigate',
              next: '',
              payload: {},
              method: 'GET',
              endpoint: ''
            },
            data: {
              url: child.url || '',
              filename: child.filename || 'document.pdf'
            }
          });
          break;

        case 'PhotoPicker':
        case 'photo_picker':
          const photoPickerData = {
            label: child.label || '請選擇照片',
            photo_source: child['photo-source'] || 'camera_gallery',
            max_file_size_kb: child['max-file-size-kb'] ?? 25600, // Meta API 限制：單個組件的 max-file-size-kb 最大值為 25600 KB，所有組件的總和不能超過 102400 KB
            min_uploaded_photos: child['min-uploaded-photos'] ?? 0,
            max_uploaded_photos: child['max-uploaded-photos'] ?? 3, // 默認值：3 張照片
            enabled: child.enabled !== undefined ? child.enabled : true,
            visible: child.visible !== undefined ? child.visible : true
          };
          
          // description: 只在有值時才包含
          if (child.description && child.description.trim() !== '') {
            photoPickerData.description = child.description;
          }
          
          // error-message: 如果是對象，轉換為內部格式；如果是字符串且非空，轉換為對象
          if (child['error-message']) {
            if (typeof child['error-message'] === 'object') {
              photoPickerData.error_message = child['error-message'];
            } else if (typeof child['error-message'] === 'string' && child['error-message'].trim() !== '') {
              photoPickerData.error_message = { text: child['error-message'] };
            }
          }
          
          data.actions.push({
            type: 'photo_picker',
            id: child.id || `photo_${(child.name || 'picker').replace(/[^a-zA-Z0-9_]/g, '_')}`,
            name: child.name || `photo_picker_${Date.now()}`,
            title: child.label || '照片選擇器',
            action: {
              type: 'submit',
              payload: {}
            },
            data: photoPickerData
          });
          break;

        case 'DocumentPicker':
        case 'document_picker':
          const documentPickerData = {
            label: child.label || '請選擇文檔',
            max_file_size_kb: child['max-file-size-kb'] ?? 25600, // Meta API 限制：單個組件的 max-file-size-kb 最大值為 25600 KB，所有組件的總和不能超過 102400 KB
            min_uploaded_documents: child['min-uploaded-documents'] ?? 0,
            max_uploaded_documents: child['max-uploaded-documents'] ?? 10, // Meta API 限制：所有組件的總文件數量不能超過 10
            allowed_mime_types: child['allowed-mime-types'] || ['application/pdf', 'image/jpeg', 'image/png'],
            enabled: child.enabled !== undefined ? child.enabled : true,
            visible: child.visible !== undefined ? child.visible : true
          };
          
          // description: 只在有值時才包含
          if (child.description && child.description.trim() !== '') {
            documentPickerData.description = child.description;
          }
          
          // error-message: 如果是對象，轉換為內部格式；如果是字符串且非空，轉換為對象
          if (child['error-message']) {
            if (typeof child['error-message'] === 'object') {
              documentPickerData.error_message = child['error-message'];
            } else if (typeof child['error-message'] === 'string' && child['error-message'].trim() !== '') {
              documentPickerData.error_message = { text: child['error-message'] };
            }
          }
          
          data.actions.push({
            type: 'document_picker',
            id: child.id || `doc_${(child.name || 'picker').replace(/[^a-zA-Z0-9_]/g, '_')}`,
            name: child.name || `document_picker_${Date.now()}`,
            title: child.label || '文檔選擇器',
            action: {
              type: 'submit',
              payload: {}
            },
            data: documentPickerData
          });
          break;

        case 'EmbeddedLink':
        case 'embedded_link':
          data.actions.push({
            type: 'embedded_link',
            text: child.text || '',
            action: child['on-click-action'] ? {
              type: 'url',
              endpoint: child['on-click-action'].url || '',
              payload: child['on-click-action'].payload || {},
              method: 'GET'
            } : {
              type: 'url',
              endpoint: '',
              payload: {},
              method: 'GET'
            },
            data: null
          });
          break;

        case 'OptIn':
        case 'opt_in':
          data.actions.push({
            type: 'opt_in',
            label: child.label || '',
            name: child.name || 'opt_in',
            action: child['on-click-action'] ? {
              type: 'url',
              endpoint: child['on-click-action'].url || '',
              payload: child['on-click-action'].payload || {},
              method: 'GET'
            } : {
              type: 'url',
              endpoint: '',
              payload: {},
              method: 'GET'
            },
            data: null
          });
          break;

        case 'If':
        case 'if':
          data.actions.push({
            type: 'if',
            key: child.key || '${form.field_name}',
            components: (child.components || []).map(comp => {
              const tempScreen = { layout: { children: [comp] } };
              const converted = convertLayoutToDataFormat(tempScreen);
              return converted.actions[0] || null;
            }).filter(Boolean)
          });
          break;

        case 'Switch':
        case 'switch':
          data.actions.push({
            type: 'switch',
            key: child.key || '${form.field_name}',
            cases: (child.cases || []).map(caseItem => ({
              key: caseItem.key || '',
              components: (caseItem.components || []).map(comp => {
                const tempScreen = { layout: { children: [comp] } };
                const converted = convertLayoutToDataFormat(tempScreen);
                return converted.actions[0] || null;
              }).filter(Boolean)
            }))
          });
          break;

        case 'CalendarPicker':
        case 'calendar_picker':
          const calendarPickerData = {
            required: child.required || false
          };
          
          if (child.enabled !== undefined) {
            calendarPickerData.enabled = child.enabled;
          }
          if (child.visible !== undefined) {
            calendarPickerData.visible = child.visible;
          }
          if (child.description && child.description.trim() !== '') {
            calendarPickerData.description = child.description;
          }
          if (child['error-message']) {
            if (typeof child['error-message'] === 'object') {
              calendarPickerData.error_message = child['error-message'];
            } else if (typeof child['error-message'] === 'string' && child['error-message'].trim() !== '') {
              calendarPickerData.error_message = { text: child['error-message'] };
            }
          }
          
          data.actions.push({
            type: 'calendar_picker',
            name: child.name || `calendar_picker_${Date.now()}`,
            title: child.label || '日曆選擇',
            action: child['on-select-action'] ? {
              type: child['on-select-action'].name === 'complete' || child['on-select-action'].name === 'data_exchange' ? 'submit' : 
                    (child['on-select-action'].name === 'navigate' ? 'navigate' : 'url'),
              next: child['on-select-action'].next?.name || '',
              payload: child['on-select-action'].payload || {},
              method: 'GET',
              endpoint: child['on-select-action'].url || ''
            } : {
              type: 'submit',
              next: '',
              payload: {},
              method: 'GET',
              endpoint: ''
            },
            data: calendarPickerData
          });
          break;

        case 'NavigationList':
        case 'navigation_list':
          data.actions.push({
            type: 'navigation_list',
            id: child.id || `navigation_list_${Date.now()}`,
            title: '導航列表',
            data: {
              items: (child.items || []).map(item => ({
                id: item.id || '',
                title: item.title || '',
                description: item.description || '',
                'on-click-action': item['on-click-action'] || null
              }))
            },
            action: {
              type: 'navigate',
              next: '',
              payload: {},
              method: 'GET',
              endpoint: ''
            }
          });
          break;

        case 'ChipsSelector':
        case 'chips_selector':
          // ChipsSelector 使用 data-source（內聯數組），不是 options
          const chipsDataSource = child['data-source'] || child.options || [];
          const chipsOptions = Array.isArray(chipsDataSource) 
            ? chipsDataSource.map(opt => ({
                id: opt.id || '',
                title: opt.title || ''
              }))
            : [];
          
          data.actions.push({
            type: 'chips_selector',
            id: child.id || child.name || `chips_selector_${Date.now()}`,
            name: child.name || child.id || `chips_selector_${Date.now()}`,
            title: child.label || '小標籤選擇器',
            data: {
              options: chipsOptions,
              required: child.required || false,
              max_selected_items: child['max-selected-items'] || child['max_selected_items'],
              description: child.description || ''
            },
            action: child['on-select-action'] || child['on-click-action'] ? {
              type: (child['on-select-action'] || child['on-click-action']).name === 'complete' || 
                    (child['on-select-action'] || child['on-click-action']).name === 'data_exchange' ? 'submit' : 
                    ((child['on-select-action'] || child['on-click-action']).name === 'navigate' ? 'navigate' : 'url'),
              next: (child['on-select-action'] || child['on-click-action']).next?.name || '',
              payload: (child['on-select-action'] || child['on-click-action']).payload || {},
              method: 'GET',
              endpoint: (child['on-select-action'] || child['on-click-action']).url || ''
            } : {
              type: 'submit',
              next: '',
              payload: {},
              method: 'GET',
              endpoint: ''
            }
          });
          break;

        case 'ImageCarousel':
        case 'image_carousel':
          // 根據官方文檔：ImageCarousel 不需要 id 和 action
          // 參考：https://developers.facebook.com/docs/whatsapp/flows/reference/components#image_carousel
          data.actions.push({
            type: 'image_carousel',
            id: `image_carousel_${Date.now()}`, // 內部使用 id 用於 UI 識別
            title: '圖片輪播',
            data: {
              images: (child.images || []).map(img => {
                const imgSrc = img.src || '';
                // 如果 src 是純 base64，構建完整 data URL 用於編輯器預覽
                const fullDataUrl = imgSrc.startsWith('data:image/') ? imgSrc : `data:image/png;base64,${imgSrc}`;
                return {
                  src: imgSrc, // 保存純 base64
                  url: fullDataUrl, // 保存完整 data URL 用於預覽
                  'alt-text': img['alt-text'] || img.alt || ''
                };
              }),
              'aspect-ratio': child['aspect-ratio'] || '4:3',
              'scale-type': child['scale-type'] || 'contain'
            }
          });
          break;
      }
    });
  }

  // 將 screen.data 中的數據模型保存到 data.dataModel 中，以便在生成 JSON 時使用
  const result = {
    id: screen.id || createDefaultScreenId(),
    title: screen.title || '',
    data: data
  };
  
  // 如果 screen.data 存在，保存數據模型定義
  // 注意：Meta API 返回的 screen.data 可能包含 body、footer、header、actions 等字段（可能為 null），
  // 這些字段不應該保存到 dataModel 中，只保留數據模型定義（如 dropdown_select、checkbox_checkbox 等）
  if (screen.data) {
    const filteredDataModel = {};
    
    // 只保留數據模型定義（不包含 body、footer、header、actions）
    Object.keys(screen.data).forEach(key => {
      if (key !== 'body' && key !== 'footer' && key !== 'header' && key !== 'actions') {
        filteredDataModel[key] = screen.data[key];
      }
    });
    
    // 只有當過濾後的數據模型不為空時，才保存
    if (Object.keys(filteredDataModel).length > 0) {
      result.data.dataModel = filteredDataModel;
    }
  }
  
  return result;
};

/**
 * 將 Meta Flow JSON 解析為編輯器數據
 */
export const parseMetaFlowJson = (json) => {
  try {
    let flowData;
    if (typeof json === 'string') {
      flowData = JSON.parse(json);
    } else {
      flowData = json;
    }
    
    if (!flowData || typeof flowData !== 'object') {
      throw new Error('無效的 JSON 格式');
    }
    
    const editorData = {
      name: flowData.name || '',
      categories: flowData.categories || ['LEAD_GENERATION'],
      screens: (flowData.screens || []).map(screen => convertLayoutToDataFormat(screen))
    };
    
    return editorData;
  } catch (error) {
    console.error('解析 Meta Flow JSON 失敗:', error);
    throw error;
  }
};

/**
 * 驗證 Meta Flow JSON 格式
 * 返回 { valid: boolean, errors: string[] }
 */
export const validateMetaFlowJson = (json) => {
  const errors = [];
  
  try {
    let flowData;
    if (typeof json === 'string') {
      try {
        flowData = JSON.parse(json);
      } catch (parseError) {
        errors.push(`JSON 解析失敗: ${parseError.message}`);
        return { valid: false, errors };
      }
    } else {
      flowData = json;
    }
    
    if (!flowData || typeof flowData !== 'object') {
      errors.push('無效的 JSON 格式：必須是對象');
      return { valid: false, errors };
    }
    
    // 檢查 version
    if (!flowData.version) {
      errors.push('缺少必需字段: version');
    } else if (typeof flowData.version !== 'string') {
      errors.push('version 必須是字符串');
    }
    
    // 檢查 screens
    if (!flowData.screens) {
      errors.push('缺少必需字段: screens');
    } else if (!Array.isArray(flowData.screens)) {
      errors.push('screens 必須是數組');
    } else if (flowData.screens.length === 0) {
      errors.push('screens 數組不能為空');
    } else {
      // 驗證每個 screen
      flowData.screens.forEach((screen, index) => {
        if (!screen.id) {
          errors.push(`Screen[${index}]: 缺少 id 字段`);
        } else if (typeof screen.id !== 'string') {
          errors.push(`Screen[${index}]: id 必須是字符串`);
        } else {
          // 驗證 id 格式（Meta 編輯器要求僅允許字母與底線）
          if (!/^[a-zA-Z_]+$/.test(screen.id)) {
            errors.push(`Screen[${index}]: id "${screen.id}" 只能包含字母和下劃線`);
          }
        }
        
        if (!screen.layout) {
          errors.push(`Screen[${index}]: 缺少 layout 字段`);
        } else {
          if (screen.layout.type !== 'SingleColumnLayout') {
            errors.push(`Screen[${index}]: layout.type 必須是 "SingleColumnLayout"`);
          }
          
          if (!screen.layout.children) {
            errors.push(`Screen[${index}]: layout.children 字段缺失`);
          } else if (!Array.isArray(screen.layout.children)) {
            errors.push(`Screen[${index}]: layout.children 必須是數組`);
          } else {
            // 檢查必須有 TextBody
            const hasTextBody = screen.layout.children.some(child => child.type === 'TextBody');
            if (!hasTextBody) {
              errors.push(`Screen[${index}]: 必須包含至少一個 TextBody 組件`);
            }
            
            // 檢查每個 screen 只能有一個 PhotoPicker 或 DocumentPicker
            const photoPickerCount = screen.layout.children.filter(c => c.type === 'PhotoPicker').length;
            const documentPickerCount = screen.layout.children.filter(c => c.type === 'DocumentPicker').length;
            if (photoPickerCount > 1) {
              errors.push(`Screen[${index}]: 每個 screen 只能有一個 PhotoPicker 組件`);
            }
            if (documentPickerCount > 1) {
              errors.push(`Screen[${index}]: 每個 screen 只能有一個 DocumentPicker 組件`);
            }
            if (photoPickerCount > 0 && documentPickerCount > 0) {
              errors.push(`Screen[${index}]: 不能同時使用 PhotoPicker 和 DocumentPicker 組件`);
            }
            
            // 驗證每個組件
            screen.layout.children.forEach((child, childIndex) => {
              if (!child.type) {
                errors.push(`Screen[${index}], Component[${childIndex}]: 缺少 type 字段`);
              } else {
                // 使用規範配置驗證組件
                const spec = getComponentSpec(child.type);
                if (!spec) {
                  const validTypes = Object.keys(COMPONENT_SPECS).join(', ');
                  errors.push(`Screen[${index}], Component[${childIndex}]: 無效的組件類型 "${child.type}"。支持的類型: ${validTypes}`);
                } else {
                  // 使用規範配置驗證組件
                  const componentValidation = validateComponent(child, spec);
                  if (!componentValidation.valid) {
                    componentValidation.errors.forEach(error => {
                      errors.push(`Screen[${index}], Component[${childIndex}] (${child.type}): ${error}`);
                    });
                  }
                  
                  // 驗證 PhotoPicker 特定屬性
                  if (child.type === 'PhotoPicker') {
                    if (child['min-uploaded-photos'] !== undefined && child['max-uploaded-photos'] !== undefined) {
                      if (child['min-uploaded-photos'] > child['max-uploaded-photos']) {
                        errors.push(`Screen[${index}], Component[${childIndex}] (PhotoPicker): min-uploaded-photos 不能大於 max-uploaded-photos`);
                      }
                    }
                    if (child['photo-source'] && !['camera_gallery', 'camera', 'gallery'].includes(child['photo-source'])) {
                      errors.push(`Screen[${index}], Component[${childIndex}] (PhotoPicker): photo-source 必須是 'camera_gallery', 'camera' 或 'gallery'`);
                    }
                    // Meta API 限制：單個組件的 max-file-size-kb 最大值為 25600 KB
                    if (child['max-file-size-kb'] !== undefined && child['max-file-size-kb'] > 25600) {
                      errors.push(`Screen[${index}], Component[${childIndex}] (PhotoPicker): max-file-size-kb 不能超過 25600 KB`);
                    }
                  }
                  
                  // 驗證 DocumentPicker 特定屬性
                  if (child.type === 'DocumentPicker') {
                    if (child['min-uploaded-documents'] !== undefined && child['max-uploaded-documents'] !== undefined) {
                      if (child['min-uploaded-documents'] > child['max-uploaded-documents']) {
                        errors.push(`Screen[${index}], Component[${childIndex}] (DocumentPicker): min-uploaded-documents 不能大於 max-uploaded-documents`);
                      }
                    }
                    // Meta API 限制：單個組件的 max-file-size-kb 最大值為 25600 KB
                    if (child['max-file-size-kb'] !== undefined && child['max-file-size-kb'] > 25600) {
                      errors.push(`Screen[${index}], Component[${childIndex}] (DocumentPicker): max-file-size-kb 不能超過 25600 KB`);
                    }
                    // 驗證 description: 如果提供，不能是空字符串
                    if (child.description !== undefined && child.description !== null && child.description.trim() === '') {
                      errors.push(`Screen[${index}], Component[${childIndex}] (DocumentPicker): description 不能是空字符串，如果不提供請移除該屬性`);
                    }
                    // 驗證 error-message: 如果提供，必須是對象且不能是空字符串
                    if (child['error-message'] !== undefined) {
                      if (typeof child['error-message'] === 'string' && child['error-message'].trim() === '') {
                        errors.push(`Screen[${index}], Component[${childIndex}] (DocumentPicker): error-message 不能是空字符串，如果不提供請移除該屬性`);
                      } else if (typeof child['error-message'] !== 'object') {
                        errors.push(`Screen[${index}], Component[${childIndex}] (DocumentPicker): error-message 必須是對象類型`);
                      }
                    }
                  }
                  
                  // 驗證 TextInput 特定屬性
                  if (child.type === 'TextInput') {
                    if (child.id) {
                      errors.push(`Screen[${index}], Component[${childIndex}] (TextInput): 不允許使用 id 屬性，應使用 name`);
                    }
                    if (child.placeholder) {
                      errors.push(`Screen[${index}], Component[${childIndex}] (TextInput): 不允許使用 placeholder 屬性`);
                    }
                    if (child['on-click-action']) {
                      errors.push(`Screen[${index}], Component[${childIndex}] (TextInput): 不允許使用 on-click-action 屬性`);
                    }
                  }
                  
                  // 驗證 TextArea 特定屬性（與 TextInput 類似，在 Form 內部）
                  if (child.type === 'TextArea') {
                    if (child.id) {
                      errors.push(`Screen[${index}], Component[${childIndex}] (TextArea): 不允許使用 id 屬性，應使用 name`);
                    }
                    if (child['on-click-action']) {
                      errors.push(`Screen[${index}], Component[${childIndex}] (TextArea): 不允許使用 on-click-action 屬性`);
                    }
                  }
                  
                  // 驗證 DatePicker 和 CalendarPicker 特定屬性
                  if (child.type === 'DatePicker' || child.type === 'CalendarPicker') {
                    if (child.id) {
                      errors.push(`Screen[${index}], Component[${childIndex}] (${child.type}): 不允許使用 id 屬性，應使用 name`);
                    }
                    if (child.placeholder) {
                      errors.push(`Screen[${index}], Component[${childIndex}] (${child.type}): 不允許使用 placeholder 屬性`);
                    }
                    if (child['on-click-action']) {
                      errors.push(`Screen[${index}], Component[${childIndex}] (${child.type}): 不允許使用 on-click-action 屬性，應使用 on-select-action`);
                    }
                  }
                  
                  // 驗證 Dropdown 特定屬性
                  if (child.type === 'Dropdown') {
                    if (child.id) {
                      errors.push(`Screen[${index}], Component[${childIndex}] (Dropdown): 不允許使用 id 屬性，應使用 name`);
                    }
                    if (!child['data-source']) {
                      errors.push(`Screen[${index}], Component[${childIndex}] (Dropdown): 缺少必需的 data-source 屬性`);
                    }
                    if (child.options) {
                      errors.push(`Screen[${index}], Component[${childIndex}] (Dropdown): 不允許使用 options 屬性，應使用 data-source`);
                    }
                    if (child['on-click-action']) {
                      errors.push(`Screen[${index}], Component[${childIndex}] (Dropdown): 不允許使用 on-click-action 屬性，應使用 on-select-action`);
                    }
                    if (child['on-select-action']) {
                      const actionName = child['on-select-action'].name;
                      if (actionName !== 'update_data' && actionName !== 'data_exchange') {
                        errors.push(`Screen[${index}], Component[${childIndex}] (Dropdown): on-select-action.name 應該是 "update_data" 或 "data_exchange"，當前值: "${actionName}"`);
                      }
                    }
                  }
                  
                  // 驗證 CheckboxGroup 特定屬性
                  if (child.type === 'CheckboxGroup') {
                    if (child.id) {
                      errors.push(`Screen[${index}], Component[${childIndex}] (CheckboxGroup): 不允許使用 id 屬性，應使用 name`);
                    }
                    if (!child.name) {
                      errors.push(`Screen[${index}], Component[${childIndex}] (CheckboxGroup): 缺少必需的 name 屬性`);
                    }
                    if (!child['data-source']) {
                      errors.push(`Screen[${index}], Component[${childIndex}] (CheckboxGroup): 缺少必需的 data-source 屬性`);
                    }
                    if (child.options) {
                      errors.push(`Screen[${index}], Component[${childIndex}] (CheckboxGroup): 不允許使用 options 屬性，應使用 data-source`);
                    }
                    if (child['on-click-action']) {
                      errors.push(`Screen[${index}], Component[${childIndex}] (CheckboxGroup): 不允許使用 on-click-action 屬性，應使用 on-select-action`);
                    }
                    if (child['on-select-action']) {
                      const actionName = child['on-select-action'].name;
                      if (actionName !== 'update_data' && actionName !== 'data_exchange') {
                        errors.push(`Screen[${index}], Component[${childIndex}] (CheckboxGroup): on-select-action.name 應該是 "update_data" 或 "data_exchange"，當前值: "${actionName}"`);
                      }
                    }
                  }
                  
                  // 驗證 RadioButtonsGroup 特定屬性
                  if (child.type === 'RadioButtonsGroup') {
                    if (child.id) {
                      errors.push(`Screen[${index}], Component[${childIndex}] (RadioButtonsGroup): 不允許使用 id 屬性，應使用 name`);
                    }
                    if (!child.name) {
                      errors.push(`Screen[${index}], Component[${childIndex}] (RadioButtonsGroup): 缺少必需的 name 屬性`);
                    }
                    if (!child['data-source']) {
                      errors.push(`Screen[${index}], Component[${childIndex}] (RadioButtonsGroup): 缺少必需的 data-source 屬性`);
                    }
                    if (child.options) {
                      errors.push(`Screen[${index}], Component[${childIndex}] (RadioButtonsGroup): 不允許使用 options 屬性，應使用 data-source`);
                    }
                    if (child['on-click-action']) {
                      errors.push(`Screen[${index}], Component[${childIndex}] (RadioButtonsGroup): 不允許使用 on-click-action 屬性，應使用 on-select-action`);
                    }
                    if (child['on-select-action']) {
                      const actionName = child['on-select-action'].name;
                      if (actionName !== 'update_data' && actionName !== 'data_exchange') {
                        errors.push(`Screen[${index}], Component[${childIndex}] (RadioButtonsGroup): on-select-action.name 應該是 "update_data" 或 "data_exchange"，當前值: "${actionName}"`);
                      }
                    }
                  }
                }
                
                // 驗證 Footer 必須有 on-click-action
                if (child.type === 'Footer') {
                  if (!child['on-click-action']) {
                    errors.push(`Screen[${index}], Component[${childIndex}] (Footer): 必須包含 on-click-action`);
                  } else {
                    const action = child['on-click-action'];
                    if (!action.name) {
                      errors.push(`Screen[${index}], Component[${childIndex}] (Footer): on-click-action 必須包含 name`);
                    } else if (action.name !== 'complete') {
                      errors.push(`Screen[${index}], Component[${childIndex}] (Footer): on-click-action.name 必須是 "complete"`);
                    }
                  }
                }
                
                  // 驗證 Image 特定屬性
                  if (child.type === 'Image') {
                    if (child.id) {
                      errors.push(`Screen[${index}], Component[${childIndex}] (Image): 不允許使用 id 屬性`);
                    }
                    if (child.url) {
                      errors.push(`Screen[${index}], Component[${childIndex}] (Image): 不允許使用 url 屬性，應使用 src`);
                    }
                    if (child.alt) {
                      errors.push(`Screen[${index}], Component[${childIndex}] (Image): 不允許使用 alt 屬性`);
                    }
                    if (child['on-click-action']) {
                      errors.push(`Screen[${index}], Component[${childIndex}] (Image): 不允許使用 on-click-action 屬性`);
                    }
                  }
                  
                // 驗證其他組件的 on-click-action（排除 Image）
                if (child['on-click-action'] && child.type !== 'Image') {
                  const action = child['on-click-action'];
                  if (!action.name) {
                    errors.push(`Screen[${index}], Component[${childIndex}] (${child.type}): on-click-action 必須包含 name`);
                  } else {
                    const validActionNames = ['data_exchange', 'navigate', 'open_url', 'complete'];
                    if (!validActionNames.includes(action.name)) {
                      errors.push(`Screen[${index}], Component[${childIndex}] (${child.type}): on-click-action.name "${action.name}" 無效，必須是: ${validActionNames.join(', ')}`);
                    }
                    
                    // 驗證 navigate action 的 next 格式
                    if (action.name === 'navigate') {
                      if (!action.next) {
                        errors.push(`Screen[${index}], Component[${childIndex}] (${child.type}): navigate action 必須包含 next`);
                      } else if (typeof action.next === 'string') {
                        errors.push(`Screen[${index}], Component[${childIndex}] (${child.type}): navigate action 的 next 必須是對象 { name: string, type: "screen" }`);
                      } else if (typeof action.next === 'object') {
                        if (!action.next.name || typeof action.next.name !== 'string') {
                          errors.push(`Screen[${index}], Component[${childIndex}] (${child.type}): navigate action 的 next.name 必須是字符串`);
                        }
                        if (action.next.type !== 'screen') {
                          errors.push(`Screen[${index}], Component[${childIndex}] (${child.type}): navigate action 的 next.type 必須是 "screen"`);
                        }
                      }
                    }
                  }
                }
              }
            });
          }
        }
      });
    }
    
    // 總體驗證：檢查所有 PhotoPicker 和 DocumentPicker 的總文件數量和總文件大小限制
    // 根據 Meta API 限制：
    // 1. 總文件數量不能超過 10（所有 PhotoPicker 和 DocumentPicker 的 max-uploaded 總和）
    // 2. 總文件大小不能超過 102400 KB（所有 PhotoPicker 和 DocumentPicker 的 max-file-size-kb 總和）
    // 如果沒有設置，默認值是 30 個文件和 25600 KB 每個文件
    if (flowData.screens && Array.isArray(flowData.screens)) {
      let totalMaxFiles = 0;
      let totalMaxSizeKB = 0;
      const MAX_TOTAL_FILES = 10;
      const MAX_TOTAL_SIZE_KB = 102400;
      
      const countMediaComponents = (children) => {
        children.forEach(child => {
          if (child.type === 'PhotoPicker') {
            const maxUploaded = child['max-uploaded-photos'] ?? 3;
            const maxFileSize = child['max-file-size-kb'] ?? 25600;
            totalMaxFiles += maxUploaded;
            totalMaxSizeKB += maxFileSize;
          } else if (child.type === 'DocumentPicker') {
            const maxUploaded = child['max-uploaded-documents'] ?? 10;
            const maxFileSize = child['max-file-size-kb'] ?? 25600;
            totalMaxFiles += maxUploaded;
            totalMaxSizeKB += maxFileSize;
          } else if (child.type === 'Form' && child.children) {
            countMediaComponents(child.children);
          }
        });
      };
      flowData.screens.forEach((screen, screenIndex) => {
        if (screen.layout && screen.layout.children && Array.isArray(screen.layout.children)) {
          countMediaComponents(screen.layout.children);
        }
      });
      
      if (totalMaxFiles > MAX_TOTAL_FILES) {
        errors.push(`Meta API 限制違規: 所有 PhotoPicker 和 DocumentPicker 的總文件數量 (${totalMaxFiles}) 超過限制 (${MAX_TOTAL_FILES})。請調整各組件的 max-uploaded-photos 或 max-uploaded-documents 值，使總和不超過 ${MAX_TOTAL_FILES}。`);
      }
      
      if (totalMaxSizeKB > MAX_TOTAL_SIZE_KB) {
        errors.push(`Meta API 限制違規: 所有 PhotoPicker 和 DocumentPicker 的總文件大小 (${totalMaxSizeKB} KB) 超過限制 (${MAX_TOTAL_SIZE_KB} KB)。請調整各組件的 max-file-size-kb 值，使總和不超過 ${MAX_TOTAL_SIZE_KB} KB。`);
      }
    }
    
    return {
      valid: errors.length === 0,
      errors: errors
    };
  } catch (error) {
    errors.push(`驗證過程發生錯誤: ${error.message}`);
    console.error('驗證 Meta Flow JSON 失敗:', error);
    return { valid: false, errors };
  }
};

/**
 * 創建 Meta Flow 創建請求
 */
export const createMetaFlowRequest = (flowData) => {
  const metaFlowJson = generateMetaFlowJson(flowData);
  
  return {
    name: flowData.name,
    categories: flowData.categories || ['LEAD_GENERATION'],
    ...metaFlowJson
  };
};

/**
 * 創建 Meta Flow 更新請求
 */
export const createMetaFlowUpdateRequest = (flowData) => {
  const metaFlowJson = generateMetaFlowJson(flowData);
  
  return {
    name: flowData.name,
    categories: flowData.categories || ['LEAD_GENERATION'],
    ...metaFlowJson
  };
};
